import { 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  onSnapshot,
  Unsubscribe,
  setDoc,
  doc,
  collection,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { getTenantCollection, getTenantDocument } from '../firebase/config';
import { db } from '../firebase/config';
import { allocateInvoiceNumber, allocateOrderId } from './saleCounters';
import type { CheckoutIdempotencyScope } from '../utils/checkoutIdempotency';

// Generic interface for documents
interface BaseDocument {
  id?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

// Order item interface
export interface OrderItem {
  id: string;
  menuItemId: string;
  menuItemName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  status: 'requested' | 'preparing' | 'completed';
  notes?: string;
  
  // Add-on selections
  addOnSelections?: Array<{
    categoryId: string;
    categoryName: string;
    selectedOptions: Array<{
      optionId: string;
      optionName: string;
      price: number;
    }>;
  }>;
  
  // Add-ons signature for tracking unique combinations
  addOnsSignature?: string; // Unique identifier for add-ons combination
  
  // Calculated fields
  addOnsTotal?: number; // Total cost of all selected add-ons
  finalPrice?: number; // Unit price + add-ons total
}

// Order interfaces
export interface Order extends BaseDocument {
  customer: string;
  customerId: string;
  customerPhone?: string;
  items: OrderItem[];
  subtotal: number;
  tax: number;
  total: number;
  status: 'active' | 'completed' | 'cancelled';
  isPaid: boolean;
  paidAmount: number;
  remainingAmount: number;
  notes?: string;
  tableNumber?: string;
  tableId?: string; // Reference to the table document ID
  tableStatus?: 'vacant' | 'occupied'; // Current table status for this order
  /** Persisted bill number allocated when order is fully paid */
  invoiceNumber?: string;
  invoiceIssuedAt?: Date;
  invoiceSequence?: number;
  /** Client idempotency key for the checkout that created this order */
  checkoutIdempotencyKey?: string;
}

// Inventory interfaces
export interface InventoryItem extends BaseDocument {
  name: string;
  category: string;
  currentStock: number;
  minStock: number;
  unit: string;
  price: number;
  supplier: string;
  lastRestocked: Date;
}

// Raw Material interface with enhanced sub-unit system
export interface RawMaterial extends BaseDocument {
  sku?: string; // Unique SKU identifier
  name: string;
  category: string;
  currentStock: number; // Always stored in base units (smallest unit)
  minStock: number; // In base units
  maxStock: number; // In base units
  
  // Main unit system (what you purchase/store in)
  mainUnit: string; // e.g., "box", "carton", "bag"
  mainUnitQuantity: number; // How many you have in main units
  
  // Sub-unit system (smallest sellable unit)
  subUnit: string; // e.g., "piece", "bottle", "stick"
  subUnitsPerMainUnit: number; // e.g., 20 pieces per box
  
  // Cost information
  costPerMainUnit: number; // Cost when buying in main units
  costPerSubUnit: number; // Calculated: costPerMainUnit / subUnitsPerMainUnit
  
  // Supplier and other info
  supplier?: string;
  supplierContact?: string;
  lastRestocked?: Date;
  expiryDate?: Date;
  location?: string;
  description?: string;
  active?: boolean; // Whether material is active
  
  // Legacy fields for backward compatibility
  baseUnit: string; // Will be same as subUnit
  costPerUnit: number; // Will be same as costPerSubUnit
  unitConversions: Array<{
    unitName: string;
    conversionFactor: number;
    isPurchaseUnit: boolean;
    isSaleUnit: boolean;
    costPerConversionUnit?: number;
  }>;
  defaultPurchaseUnit: string;
  defaultSaleUnit: string;
}

// Menu Item interface with flexible unit support
export interface MenuItem extends BaseDocument {
  name: string;
  category: string;
  price: number;
  description?: string;
  ingredients: Array<{
    rawMaterialId: string;
    quantity: number;
    unit: string; // Unit for this ingredient (must match rawMaterial's base unit or conversion units)
  }>;
  preparationTime: number; // in minutes
  isAvailable: boolean;
  autoDeductInventory: boolean; // Whether to automatically deduct ingredients when sold
  image?: string;
  
  // Sale unit configuration
  saleUnit: string; // Unit in which this menu item is sold (e.g., "piece", "box of 10")
  saleUnitConversion?: number; // If different from base unit, how many base units per sale unit
  
  // Enhanced fields for public menu features
  isFeatured?: boolean; // Whether this item is featured/highlighted
  isSpecialOffer?: boolean; // Whether this item has a special offer
  isTodaysSpecial?: boolean; // Whether this item is marked as today's special
  originalPrice?: number; // Original price if there's a discount
  popularityScore?: number; // Score based on sales frequency (higher = more popular)
  badges?: string[]; // Custom badges like "Chef's Special", "New", "Bestseller", etc.
  dietaryInfo?: string[]; // "Vegetarian", "Vegan", "Gluten-Free", "Spicy", etc.
  spiceLevel?: 1 | 2 | 3 | 4 | 5; // Spice level for food items (1 = mild, 5 = very spicy)
  tags?: string[]; // Additional tags for searching/filtering
  
  // Add-on support
  supportsAddOns?: boolean; // Whether this item supports add-ons
  addOnCategories?: string[]; // Array of add-on category IDs
}

// Inventory Transaction interface
export interface InventoryTransaction extends BaseDocument {
  itemId: string;
  itemName: string;
  type: 'in' | 'out';
  quantity: number;
  unit: string;
  reason: string;
  notes?: string;
  performedBy: string;
  referenceNumber?: string;
  supplier?: string; // for 'in' transactions
  costPerUnit?: number; // for 'in' transactions
  totalCost?: number; // for 'in' transactions
  transactionSource: 'manual' | 'sale' | 'adjustment'; // Source of the transaction
  orderId?: string; // Reference to order if this is a sale transaction
  menuItemId?: string; // Reference to menu item if this is a sale transaction
}

// Payment interfaces
export interface Payment extends BaseDocument {
  orderId: string;
  customer: string;
  customerId: string;
  amount: number;
  method: 'card' | 'cash' | 'digital';
  status: 'completed' | 'pending' | 'failed' | 'refunded';
  cardLast4?: string;
  notes?: string;
  /** Prevents duplicate payment records on retry */
  idempotencyKey?: string;
}

export interface Customer extends BaseDocument {
  name: string;
  phone?: string;
  notes?: string;
  status: 'active' | 'inactive';
  totalOrders: number;
  totalSpent: number;
  lastOrderDate?: Date;
  dateJoined: Date;
}

// Table interface for restaurant table management
export interface Table extends BaseDocument {
  tableNumber: string;
  name?: string; // Optional custom name like "Window Table"
  description?: string;
  capacity?: number;
  status: 'available' | 'occupied' | 'reserved' | 'maintenance';
  qrCodeGenerated: boolean;
  isActive: boolean;
  location?: string; // Physical location description
  notes?: string;
  currentOrderId?: string; // ID of the current active order on this table
  orderStatus?: 'vacant' | 'occupied'; // Current order status for this table
  lastOrderTime?: Date; // When the last order was placed/completed
}



// Invitation interface for worker invitations
export interface Invitation extends BaseDocument {
  token: string;
  createdBy: string;
  assignedFeatures: string[];
  expiresAt: Date;
  usedAt?: Date;
  usedBy?: string;
  status: 'active' | 'used' | 'expired';
  workerName?: string; // Optional name for the invited worker
  workerEmail?: string; // Optional email for the invited worker
}

// Notification interface
export interface Notification extends BaseDocument {
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'order' | 'payment' | 'inventory' | 'system' | 'support';
  isRead: boolean;
  priority: 'low' | 'medium' | 'high';
  actionUrl?: string; // Optional URL to navigate to when clicked
  actionText?: string; // Optional text for the action button
  metadata?: {
    orderId?: string;
    paymentId?: string;
    itemId?: string;
    customerId?: string;
    [key: string]: any;
  };
}

// Role interface for permission management
export interface Role extends BaseDocument {
  name: string;
  permissions: string[];
  isDefault: boolean;
  description?: string;
}

// Chat Message interface for chatbot conversations
export interface ChatMessage extends BaseDocument {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sessionId: string;
  queryType?: string;
  dataUsed?: string[];
  confidence?: number;
  suggestions?: string[];
}

// Chat Session interface for organizing conversations
export interface ChatSession extends BaseDocument {
  sessionId: string;
  title: string;
  messageCount: number;
  lastMessageAt: Date;
  isActive: boolean;
  summary?: string;
}

// Category interface
export interface Category extends BaseDocument {
  name: string;
  type: 'menuItem' | 'rawMaterial';
  description?: string;
  color?: string; // Optional color for UI
  isDefault?: boolean; // Whether this is a default category that can't be deleted
}

// Add-on interfaces
export interface AddOnCategory extends BaseDocument {
  name: string; // e.g., "Toppings", "Cheese", "Size", "Crust"
  description?: string;
  isRequired: boolean; // Whether customer must select at least one option
  minSelections: number; // Minimum number of selections required
  maxSelections: number; // Maximum number of selections allowed (0 = unlimited)
  displayOrder: number; // Order in which categories appear
  
  // Sub-category support
  parentCategoryId?: string; // ID of parent category (null for top-level)
  isSubCategory: boolean; // Whether this is a sub-category
  level: number; // Hierarchy level (0 = top, 1 = sub, 2 = sub-sub)
}

export interface AddOnOption extends BaseDocument {
  categoryId: string;
  name: string; // e.g., "Extra Cheese", "Mushrooms", "Pepperoni"
  description?: string;
  price: number; // Additional cost for this add-on
  isAvailable: boolean;
  displayOrder: number;
  image?: string;
  dietaryInfo?: string[]; // "Vegetarian", "Vegan", "Gluten-Free", etc.
  allergens?: string[]; // "Dairy", "Nuts", "Shellfish", etc.
}

// Menu item add-ons interface
export interface MenuItemAddOns extends BaseDocument {
  menuItemId: string;
  addOnCategories: Array<{
    categoryId: string;
    categoryName: string;
    isRequired: boolean;
    minSelections: number;
    maxSelections: number;
    displayOrder: number;
  }>;
}

class DataService {
  // Generic CRUD operations for tenant collections
  
  async create<T extends BaseDocument>(
    tenantId: string, 
    collectionName: string, 
    data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    try {
      const collectionRef = getTenantCollection(tenantId, collectionName);
      
      // Filter out undefined values
      const cleanData = Object.entries(data).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);
      
      const docData = {
        ...cleanData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      const docRef = await addDoc(collectionRef, docData);
      return docRef.id;
    } catch (error) {
      console.error(`Error creating ${collectionName} document:`, error);
      throw error;
    }
  }

  async update<T extends BaseDocument>(
    tenantId: string, 
    collectionName: string, 
    docId: string, 
    data: Partial<Omit<T, 'id' | 'createdAt'>>
  ): Promise<void> {
    try {
      const docRef = getTenantDocument(tenantId, collectionName, docId);
      
      // Filter out undefined values
      const cleanData = Object.entries(data).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);
      
      const updateData = {
        ...cleanData,
        updatedAt: Timestamp.now()
      };
      
      console.log(`🔍 [${collectionName}] Update data being sent to Firebase:`, updateData);
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error(`Error updating ${collectionName} document:`, error);
      throw error;
    }
  }

  async delete(tenantId: string, collectionName: string, docId: string): Promise<void> {
    try {
      const docRef = getTenantDocument(tenantId, collectionName, docId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error deleting ${collectionName} document:`, error);
      throw error;
    }
  }

  async getById<T extends BaseDocument>(
    tenantId: string, 
    collectionName: string, 
    docId: string
  ): Promise<T | null> {
    try {
      const docRef = getTenantDocument(tenantId, collectionName, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const result = {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        } as T;
        
        // Log any undefined values that might cause issues
        const undefinedFields = Object.entries(result).filter(([key, value]) => value === undefined);
        if (undefinedFields.length > 0) {
          console.warn(`⚠️ [${collectionName}] Document ${docId} has undefined fields:`, undefinedFields.map(([key]) => key));
        }
        
        // Filter out undefined values to prevent issues when updating
        const cleanResult = Object.entries(result).reduce((acc, [key, value]) => {
          if (value !== undefined) {
            acc[key] = value;
          }
          return acc;
        }, {} as any);
        
        // Also filter out any null values that might cause issues
        const finalResult = Object.entries(cleanResult).reduce((acc, [key, value]) => {
          if (value !== null && value !== undefined) {
            acc[key] = value;
          }
          return acc;
        }, {} as any);
        
        return finalResult as T;
      }
      
      return null;
    } catch (error) {
      console.error(`Error getting ${collectionName} document:`, error);
      throw error;
    }
  }

  async getAll<T extends BaseDocument>(
    tenantId: string, 
    collectionName: string,
    orderByField: string = 'createdAt',
    orderDirection: 'asc' | 'desc' = 'desc',
    limitCount?: number
  ): Promise<T[]> {
    try {
      const collectionRef = getTenantCollection(tenantId, collectionName);
      let q = query(collectionRef, orderBy(orderByField, orderDirection));
      
      if (limitCount) {
        q = query(q, limit(limitCount));
      }
      
      const querySnapshot = await getDocs(q);
      const documents: T[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Ensure the id field is properly set and not overwritten by document data
        const document = {
          ...data,
          id: doc.id, // Always use the document ID, not any id field in the data
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        } as T;
        
        // Filter out undefined values to prevent issues when updating
        const cleanDocument = Object.entries(document).reduce((acc, [key, value]) => {
          if (value !== undefined) {
            acc[key] = value;
          }
          return acc;
        }, {} as any);
        
        // Also filter out any null values that might cause issues
        const finalDocument = Object.entries(cleanDocument).reduce((acc, [key, value]) => {
          if (value !== null && value !== undefined) {
            acc[key] = value;
          }
          return acc;
        }, {} as any);
        
        documents.push(finalDocument as T);
      });
      
      return documents;
    } catch (error) {
      console.error(`Error getting ${collectionName} documents:`, error);
      throw error;
    }
  }

  async getByField<T extends BaseDocument>(
    tenantId: string, 
    collectionName: string,
    fieldName: string,
    fieldValue: any
  ): Promise<T[]> {
    try {
      const collectionRef = getTenantCollection(tenantId, collectionName);
      const q = query(collectionRef, where(fieldName, '==', fieldValue));
      
      const querySnapshot = await getDocs(q);
      const documents: T[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const document = {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        } as T;
        
        // Filter out undefined values to prevent issues when updating
        const cleanDocument = Object.entries(document).reduce((acc, [key, value]) => {
          if (value !== undefined) {
            acc[key] = value;
          }
          return acc;
        }, {} as any);
        
        documents.push(cleanDocument as T);
      });
      
      return documents;
    } catch (error) {
      console.error(`Error getting ${collectionName} documents by field:`, error);
      throw error;
    }
  }

  // Enhanced Order methods
  private getCheckoutIdempotencyRef(tenantId: string, idempotencyKey: string) {
    return doc(db, `tenants/${tenantId}/checkoutIdempotency/${idempotencyKey}`);
  }

  async getCheckoutIdempotencyResult(
    tenantId: string,
    idempotencyKey: string
  ): Promise<{ orderId?: string; paymentId?: string } | null> {
    const snap = await getDoc(this.getCheckoutIdempotencyRef(tenantId, idempotencyKey));
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      orderId: typeof data.orderId === 'string' ? data.orderId : undefined,
      paymentId: typeof data.paymentId === 'string' ? data.paymentId : undefined,
    };
  }

  async recordCheckoutIdempotency(
    tenantId: string,
    idempotencyKey: string,
    scope: CheckoutIdempotencyScope,
    payload: { orderId?: string; paymentId?: string }
  ): Promise<void> {
    await setDoc(
      this.getCheckoutIdempotencyRef(tenantId, idempotencyKey),
      {
        scope,
        ...payload,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );
  }

  async generateOrderId(tenantId: string): Promise<string> {
    try {
      const newOrderId = await allocateOrderId(tenantId);
      console.log(`⚡ Allocated sequential order ID: ${newOrderId}`);
      return newOrderId;
    } catch (error) {
      console.error('❌ Error allocating order ID:', error);
      throw error;
    }
  }

  async createOrder(
    tenantId: string,
    orderData: Omit<Order, 'id' | 'createdAt' | 'updatedAt'>,
    options?: { idempotencyKey?: string }
  ): Promise<string> {
    try {
      if (options?.idempotencyKey) {
        const existing = await this.getCheckoutIdempotencyResult(tenantId, options.idempotencyKey);
        if (existing?.orderId) {
          console.log(`♻️ Reusing order from idempotency key: ${existing.orderId}`);
          return existing.orderId;
        }
      }

      // Generate a sequential order ID via Firestore counter
      const orderId = await this.generateOrderId(tenantId);
      console.log(`🆔 Generated order ID: ${orderId}`);
      console.log('🔍 Order data:', orderData);
      
      // Create the order with the custom ID
      const docRef = getTenantDocument(tenantId, 'orders', orderId);
      
      // Filter out undefined values
      const cleanData = Object.entries(orderData).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);
      
      const docData = {
        ...cleanData,
        ...(options?.idempotencyKey ? { checkoutIdempotencyKey: options.idempotencyKey } : {}),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      console.log(`📝 Creating order with ID: ${orderId}`);
      console.log(`🔍 Final document data being sent to Firestore:`, docData);
      await setDoc(docRef, docData);
      console.log(`✅ Order created successfully with ID: ${orderId}`);

      if (options?.idempotencyKey) {
        await this.recordCheckoutIdempotency(tenantId, options.idempotencyKey, 'order', { orderId });
      }
      
      // Update table status if a table is assigned to this order
      if (orderData.tableId) {
        await this.updateTableOrderStatus(tenantId, orderData.tableId, orderId, 'occupied');
      }
      
      return orderId;
    } catch (error) {
      console.error('❌ Error creating order with custom ID:', error);
      console.log('🔄 Falling back to auto-generated ID');
      
      // Fallback to the original method if there's an error
      const fallbackId = await this.create<Order>(tenantId, 'orders', orderData);
      console.log(`⚠️ Created order with fallback ID: ${fallbackId}`);
      return fallbackId;
    }
  }

  /**
   * Update table order status when orders are created or completed
   */
  async updateTableOrderStatus(tenantId: string, tableId: string, orderId: string, status: 'vacant' | 'occupied'): Promise<void> {
    try {
      const updateData: Partial<Table> = {
        orderStatus: status,
        lastOrderTime: new Date()
      };
      
      if (status === 'occupied') {
        updateData.currentOrderId = orderId;
      } else {
        updateData.currentOrderId = undefined;
      }
      
      await this.updateTable(tenantId, tableId, updateData);
      console.log(`✅ Table ${tableId} status updated to: ${status}`);
    } catch (error) {
      console.error('❌ Error updating table order status:', error);
      throw error;
    }
  }

  async addItemToOrder(tenantId: string, orderId: string, item: OrderItem): Promise<void> {
    const order = await this.getById<Order>(tenantId, 'orders', orderId);
    if (!order) throw new Error('Order not found');

    const updatedItems = [...(order.items || []), item];
    const subtotal = updatedItems.reduce((sum, item) => sum + item.totalPrice, 0);
    const total = subtotal; // No tax
    const remainingAmount = total - (order.paidAmount || 0);

    const updateData: Partial<Order> = {
      items: updatedItems,
      subtotal,
      tax: 0, // No tax
      total,
      remainingAmount
    };

    console.log(`🔍 Raw update data:`, updateData);

    // Ensure no undefined values are passed to Firebase
    const cleanUpdateData = Object.entries(updateData).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    await this.update<Order>(tenantId, 'orders', orderId, cleanUpdateData);

    // Update customer stats after adding item
    await this.updateCustomerStats(tenantId, order.customer);

    // Process inventory deduction for the new item
    try {
      const result = await this.processMenuItemSale(
        tenantId,
        item.menuItemId,
        item.quantity,
        orderId,
        'Order System'
      );

      if (!result.success && result.errors.length > 0) {
        console.warn('Inventory deduction warnings:', result.errors);
        // Note: We don't throw an error here to avoid blocking the order
        // Instead, we log warnings that can be reviewed later
      }
    } catch (error) {
      console.error('Failed to process inventory deduction:', error);
      // Continue with order creation even if inventory deduction fails
    }
  }

  /**
   * Complete an order and update table status to vacant
   */
  async completeOrder(tenantId: string, orderId: string): Promise<void> {
    try {
      const order = await this.getById<Order>(tenantId, 'orders', orderId);
      if (!order) throw new Error('Order not found');
      
      // Update order status to completed
      await this.update<Order>(tenantId, 'orders', orderId, {
        status: 'completed',
        updatedAt: new Date()
      });
      
      // Update table status to vacant if this order was assigned to a table
      if (order.tableId) {
        await this.updateTableOrderStatus(tenantId, order.tableId, orderId, 'vacant');
      }
      
      console.log(`✅ Order ${orderId} completed and table status updated`);
    } catch (error) {
      console.error('❌ Error completing order:', error);
      throw error;
    }
  }

  /**
   * Cancel an order and update table status to vacant if table was assigned
   */
  async cancelOrder(tenantId: string, orderId: string): Promise<void> {
    try {
      const order = await this.getById<Order>(tenantId, 'orders', orderId);
      if (!order) throw new Error('Order not found');
      
      // Update order status to cancelled
      await this.update<Order>(tenantId, 'orders', orderId, {
        status: 'cancelled',
        updatedAt: new Date()
      });
      
      // Update table status to vacant if this order was assigned to a table
      if (order.tableId) {
        await this.updateTableOrderStatus(tenantId, order.tableId, orderId, 'vacant');
      }
      
      console.log(`✅ Order ${orderId} cancelled and table status updated`);
    } catch (error) {
      console.error('❌ Error cancelling order:', error);
      throw error;
    }
  }

  async updateOrderItemStatus(tenantId: string, orderId: string, itemId: string, status: OrderItem['status']): Promise<void> {
    const order = await this.getById<Order>(tenantId, 'orders', orderId);
    if (!order) throw new Error('Order not found');

    const updatedItems = (order.items || []).map(item => 
      item.id === itemId ? { ...item, status } : item
    );

    const updateData: Partial<Order> = {
      items: updatedItems
    };

    // Ensure no undefined values are passed to Firebase
    const cleanUpdateData = Object.entries(updateData).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    await this.update<Order>(tenantId, 'orders', orderId, cleanUpdateData);
  }

  async deleteOrderItem(tenantId: string, orderId: string, itemId: string): Promise<void> {
    const order = await this.getById<Order>(tenantId, 'orders', orderId);
    if (!order) throw new Error('Order not found');

    // Remove the item from the order
    const updatedItems = (order.items || []).filter(item => item.id !== itemId);
    
    // If no items left, delete the entire order and its payments
    if (updatedItems.length === 0) {
      // Delete all payments associated with this order
      const orderPayments = await this.getByField<Payment>(tenantId, 'payments', 'orderId', orderId);
      for (const payment of orderPayments) {
        await this.delete(tenantId, 'payments', payment.id!);
      }
      
      // Delete the order itself
      await this.delete(tenantId, 'orders', orderId);
      return;
    }
    
    // Recalculate order totals
    const subtotal = updatedItems.reduce((sum, item) => sum + item.totalPrice, 0);
    const tax = 0; // No tax in this system
    const total = subtotal + tax;
    const remainingAmount = total - (order.paidAmount || 0);

    const updateData: Partial<Order> = {
      items: updatedItems,
      subtotal,
      tax,
      total,
      remainingAmount: Math.max(0, remainingAmount),
      isPaid: remainingAmount <= 0
    };

    // Ensure no undefined values are passed to Firebase
    const cleanUpdateData = Object.entries(updateData).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    await this.update<Order>(tenantId, 'orders', orderId, cleanUpdateData);
    
    // Update customer stats after removing item
    await this.updateCustomerStats(tenantId, order.customer);
  }

  async getOrCreateActiveOrder(
    tenantId: string,
    customer: string,
    customerPhone?: string,
    tableNumber?: string,
    tableId?: string,
    options?: { idempotencyKey?: string }
  ): Promise<Order> {
    if (options?.idempotencyKey) {
      const existing = await this.getCheckoutIdempotencyResult(tenantId, options.idempotencyKey);
      if (existing?.orderId) {
        const order = await this.getById<Order>(tenantId, 'orders', existing.orderId);
        if (order) return order;
      }
    }

    // Create or update customer record and get the customer object
    const customerRecord = await this.getOrCreateCustomer(tenantId, customer, customerPhone);
    
    // Look for existing active order for this customer that has remaining balance
    const activeOrders = await this.getByField<Order>(tenantId, 'orders', 'customer', customer);
    
    console.log(`Looking for existing orders for customer: ${customer}`);
    console.log(`Found ${activeOrders.length} orders for this customer`);
    
    // Debug: Log all orders for this customer
    activeOrders.forEach((order, index) => {
      console.log(`Order ${index + 1}:`, {
        id: order.id,
        status: order.status,
        isPaid: order.isPaid,
        total: order.total,
        paidAmount: order.paidAmount,
        remainingAmount: order.remainingAmount,
        itemCount: order.items?.length || 0,
        createdAt: order.createdAt
      });
    });
    
    // Find an order that is active, not fully paid, and has remaining amount
    const existingOrder = activeOrders.find(order => {
      const isActive = order.status === 'active';
      const isNotPaid = !order.isPaid;
      const hasRemainingAmount = (order.remainingAmount || 0) > 0;
      
      console.log(`Order ${order.id}: active=${isActive}, paid=${order.isPaid}, remaining=${order.remainingAmount}`);
      
      return isActive && isNotPaid && hasRemainingAmount;
    });

    if (existingOrder) {
      console.log(`✅ Using existing order ${existingOrder.id} for customer ${customer}`);
      console.log(`Order details:`, {
        total: existingOrder.total,
        paidAmount: existingOrder.paidAmount,
        remainingAmount: existingOrder.remainingAmount,
        itemCount: existingOrder.items?.length || 0
      });
      
      // Update existing order with customer ID if missing
      if (!existingOrder.customerId) {
        await this.update<Order>(tenantId, 'orders', existingOrder.id!, { 
          customerId: customerRecord.id! 
        });
        existingOrder.customerId = customerRecord.id!;
      }
      return existingOrder;
    }

    console.log(`❌ No suitable existing order found. Creating new order for customer ${customer}`);
    console.log(`Reasons why existing orders were rejected:`);
    activeOrders.forEach(order => {
      const reasons = [];
      if (order.status !== 'active') reasons.push(`status is '${order.status}' not 'active'`);
      if (order.isPaid) reasons.push('order is marked as paid');
      if ((order.remainingAmount || 0) <= 0) reasons.push(`remaining amount is ${order.remainingAmount}`);
      
      if (reasons.length > 0) {
        console.log(`Order ${order.id} rejected: ${reasons.join(', ')}`);
      }
    });
    
    // Create new order with customer ID
    const newOrderData: Omit<Order, 'id' | 'createdAt' | 'updatedAt'> = {
      customer,
      customerId: customerRecord.id!,
      // Only include customerPhone if it's provided
      ...(customerPhone && { customerPhone }),
      items: [],
      subtotal: 0,
      tax: 0, // No tax
      total: 0,
      status: 'active',
      isPaid: false,
      paidAmount: 0,
      remainingAmount: 0,
      // Only include optional fields if they have values
      ...(tableNumber && { tableNumber }),
      ...(tableId && { tableId })
    };

    const orderId = await this.createOrder(tenantId, newOrderData, options);
    
    // Update customer stats
    await this.updateCustomerStats(tenantId, customer);
    
    return (await this.getById<Order>(tenantId, 'orders', orderId))!;
  }

  async createGuestOrder(
    tenantId: string,
    tableNumber?: string,
    tableId?: string,
    notes?: string,
    options?: { idempotencyKey?: string }
  ): Promise<Order> {
    if (options?.idempotencyKey) {
      const existing = await this.getCheckoutIdempotencyResult(tenantId, options.idempotencyKey);
      if (existing?.orderId) {
        const order = await this.getById<Order>(tenantId, 'orders', existing.orderId);
        if (order) return order;
      }
    }

    console.log(`🆕 Creating guest order for tenant: ${tenantId}`);
    
    // Create new order without customer information
    const newOrderData: Omit<Order, 'id' | 'createdAt' | 'updatedAt'> = {
      customer: 'Guest',
      customerId: '', // No customer ID for guest orders
      // Don't include customerPhone field for guest orders - it's optional
      items: [],
      subtotal: 0,
      tax: 0, // No tax
      total: 0,
      status: 'active',
      isPaid: false,
      paidAmount: 0,
      remainingAmount: 0,
      // Only include optional fields if they have values
      ...(tableNumber && { tableNumber }),
      ...(tableId && { tableId }),
      ...(notes && { notes })
    };
    console.log('🔍 New order data:', newOrderData);
    const orderId = await this.createOrder(tenantId, newOrderData, options);
    
    console.log(`✅ Guest order created successfully with ID: ${orderId}`);
    
    return (await this.getById<Order>(tenantId, 'orders', orderId))!;
  }

  async getOrders(tenantId: string): Promise<Order[]> {
    return this.getAll<Order>(tenantId, 'orders', 'createdAt', 'desc');
  }

  async getOrdersByStatus(tenantId: string, status: Order['status']): Promise<Order[]> {
    return this.getByField<Order>(tenantId, 'orders', 'status', status);
  }

  async getOrderItemsByStatus(tenantId: string, itemStatus: OrderItem['status']): Promise<{ order: Order; item: OrderItem }[]> {
    try {
      const orders = await this.getOrders(tenantId);
      const result: { order: Order; item: OrderItem }[] = [];

      if (!orders || orders.length === 0) {
        return result;
      }

      orders.forEach(order => {
        if (order.items && Array.isArray(order.items)) {
          order.items.forEach(item => {
            if (item.status === itemStatus) {
              result.push({ order, item });
            }
          });
        }
      });

      return result;
    } catch (error) {
      console.error(`Error getting order items by status:`, error);
      return [];
    }
  }

  // Payment methods
  async addPaymentToOrder(
    tenantId: string,
    orderId: string,
    paymentAmount: number,
    method: Payment['method'],
    notes?: string,
    options?: { idempotencyKey?: string }
  ): Promise<void> {
    if (options?.idempotencyKey) {
      const existing = await this.getCheckoutIdempotencyResult(tenantId, options.idempotencyKey);
      if (existing?.paymentId) {
        console.log(`♻️ Payment already recorded for idempotency key: ${options.idempotencyKey}`);
        return;
      }
    }

    const order = await this.getById<Order>(tenantId, 'orders', orderId);
    if (!order) throw new Error('Order not found');

    // Create payment record
    const paymentData: Omit<Payment, 'id' | 'createdAt' | 'updatedAt'> = {
      orderId,
      customer: order.customer,
      customerId: order.customerId,
      amount: paymentAmount,
      method,
      status: 'completed',
      ...(options?.idempotencyKey ? { idempotencyKey: options.idempotencyKey } : {}),
    };

    // Only add notes if they exist and are not empty
    if (notes && notes.trim()) {
      paymentData.notes = notes.trim();
    }

    const paymentId = await this.createPayment(tenantId, paymentData);

    if (options?.idempotencyKey) {
      await this.recordCheckoutIdempotency(tenantId, options.idempotencyKey, 'payment', {
        orderId,
        paymentId,
      });
    }

    // Update order payment status
    const newPaidAmount = order.paidAmount + paymentAmount;
    const remainingAmount = order.total - newPaidAmount;
    const isPaid = remainingAmount <= 0;

    const updateData: Partial<Order> = {
      paidAmount: newPaidAmount,
      remainingAmount: Math.max(0, remainingAmount),
      isPaid,
      status: isPaid ? 'completed' : order.status,
    };

    // Allocate persisted invoice number when order becomes fully paid
    if (isPaid && !order.invoiceNumber) {
      const { invoiceNumber, invoiceSequence } = await allocateInvoiceNumber(tenantId);
      updateData.invoiceNumber = invoiceNumber;
      updateData.invoiceSequence = invoiceSequence;
      updateData.invoiceIssuedAt = new Date();
    }

    // Ensure no undefined values are passed to Firebase
    const cleanUpdateData = Object.entries(updateData).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    await this.update<Order>(tenantId, 'orders', orderId, cleanUpdateData);
  }

  // Specific methods for Inventory
  async createInventoryItem(tenantId: string, itemData: Omit<InventoryItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    return this.create<InventoryItem>(tenantId, 'inventory', itemData);
  }

  async updateInventoryStock(tenantId: string, itemId: string, currentStock: number): Promise<void> {
    return this.update<InventoryItem>(tenantId, 'inventory', itemId, { 
      currentStock,
      lastRestocked: new Date()
    });
  }

  async getInventoryItems(tenantId: string): Promise<InventoryItem[]> {
    return this.getAll<InventoryItem>(tenantId, 'inventory', 'name', 'asc');
  }

  async getLowStockItems(tenantId: string): Promise<InventoryItem[]> {
    const items = await this.getInventoryItems(tenantId);
    return items.filter(item => item.currentStock <= item.minStock);
  }

  // Specific methods for Payments
  async createPayment(tenantId: string, paymentData: Omit<Payment, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    return this.create<Payment>(tenantId, 'payments', paymentData);
  }

  async deletePayment(tenantId: string, paymentId: string): Promise<void> {
    // Get the payment to find the associated order
    const payment = await this.getById<Payment>(tenantId, 'payments', paymentId);
    if (!payment) throw new Error('Payment not found');

    // Delete the payment
    await this.delete(tenantId, 'payments', paymentId);

    // Get the order and recalculate payment status
    const order = await this.getById<Order>(tenantId, 'orders', payment.orderId);
    if (!order) return; // Order might have been deleted

    // Get all remaining payments for this order
    const remainingPayments = await this.getByField<Payment>(tenantId, 'payments', 'orderId', payment.orderId);
    const totalPaid = remainingPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const remainingAmount = (order.total || 0) - totalPaid;

    // Update order payment status
    const updateData: Partial<Order> = {
      paidAmount: totalPaid,
      remainingAmount: Math.max(0, remainingAmount),
      isPaid: remainingAmount <= 0,
      status: remainingAmount <= 0 ? 'completed' : 'active'
    };

    // Ensure no undefined values are passed to Firebase
    const cleanUpdateData = Object.entries(updateData).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    await this.update<Order>(tenantId, 'orders', payment.orderId, cleanUpdateData);
  }

  async getPayments(tenantId: string): Promise<Payment[]> {
    return this.getAll<Payment>(tenantId, 'payments', 'createdAt', 'desc');
  }

  async getPaymentsByMethod(tenantId: string, method: Payment['method']): Promise<Payment[]> {
    return this.getByField<Payment>(tenantId, 'payments', 'method', method);
  }

  async getPaymentsByStatus(tenantId: string, status: Payment['status']): Promise<Payment[]> {
    return this.getByField<Payment>(tenantId, 'payments', 'status', status);
  }

  // Specific methods for Raw Materials
  async createRawMaterial(tenantId: string, materialData: Omit<RawMaterial, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    return this.create<RawMaterial>(tenantId, 'rawMaterials', materialData);
  }

  async updateRawMaterialStock(tenantId: string, materialId: string, currentStock: number): Promise<void> {
    return this.update<RawMaterial>(tenantId, 'rawMaterials', materialId, { 
      currentStock,
      lastRestocked: new Date()
    });
  }

  async getRawMaterials(tenantId: string): Promise<RawMaterial[]> {
    return this.getAll<RawMaterial>(tenantId, 'rawMaterials', 'name', 'asc');
  }

  async getLowStockRawMaterials(tenantId: string): Promise<RawMaterial[]> {
    const materials = await this.getRawMaterials(tenantId);
    return materials.filter(material => material.currentStock <= material.minStock);
  }

  async getRawMaterialsByCategory(tenantId: string, category: string): Promise<RawMaterial[]> {
    return this.getByField<RawMaterial>(tenantId, 'rawMaterials', 'category', category);
  }

  // Specific methods for Menu Items
  async createMenuItem(tenantId: string, menuData: Omit<MenuItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    return this.create<MenuItem>(tenantId, 'menuItems', menuData);
  }

  async getMenuItems(tenantId: string): Promise<MenuItem[]> {
    return this.getAll<MenuItem>(tenantId, 'menuItems', 'name', 'asc');
  }

  async getAvailableMenuItems(tenantId: string): Promise<MenuItem[]> {
    return this.getByField<MenuItem>(tenantId, 'menuItems', 'isAvailable', true);
  }

  async updateMenuItemAvailability(tenantId: string, menuItemId: string, isAvailable: boolean): Promise<void> {
    return this.update<MenuItem>(tenantId, 'menuItems', menuItemId, { isAvailable });
  }

  // Specific methods for Inventory Transactions
  async createInventoryTransaction(tenantId: string, transactionData: Omit<InventoryTransaction, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    return this.create<InventoryTransaction>(tenantId, 'inventoryTransactions', transactionData);
  }

  async getInventoryTransactions(tenantId: string, itemId?: string): Promise<InventoryTransaction[]> {
    if (itemId) {
      return this.getByField<InventoryTransaction>(tenantId, 'inventoryTransactions', 'itemId', itemId);
    }
    return this.getAll<InventoryTransaction>(tenantId, 'inventoryTransactions', 'createdAt', 'desc');
  }

  async getInventoryTransactionsByType(tenantId: string, type: 'in' | 'out'): Promise<InventoryTransaction[]> {
    return this.getByField<InventoryTransaction>(tenantId, 'inventoryTransactions', 'type', type);
  }

  async getInventoryTransactionsBySource(tenantId: string, source: 'manual' | 'sale' | 'adjustment'): Promise<InventoryTransaction[]> {
    return this.getByField<InventoryTransaction>(tenantId, 'inventoryTransactions', 'transactionSource', source);
  }

  async getSaleTransactions(tenantId: string): Promise<InventoryTransaction[]> {
    return this.getInventoryTransactionsBySource(tenantId, 'sale');
  }

  async getManualTransactions(tenantId: string): Promise<InventoryTransaction[]> {
    return this.getInventoryTransactionsBySource(tenantId, 'manual');
  }

  // Combined inventory operations
  async addInventoryStock(
    tenantId: string, 
    itemId: string, 
    quantity: number, 
    reason: string, 
    performedBy: string,
    supplier?: string,
    costPerUnit?: number,
    notes?: string
  ): Promise<void> {
    // Get current item
    const item = await this.getById<InventoryItem>(tenantId, 'inventory', itemId);
    if (!item) throw new Error('Inventory item not found');

    // Update stock
    const newStock = item.currentStock + quantity;
    await this.updateInventoryStock(tenantId, itemId, newStock);

    // Create transaction record
    const transactionData: Omit<InventoryTransaction, 'id' | 'createdAt' | 'updatedAt'> = {
      itemId,
      itemName: item.name,
      type: 'in',
      quantity,
      unit: item.unit,
      reason,
      performedBy,
      supplier,
      costPerUnit,
      totalCost: costPerUnit ? costPerUnit * quantity : undefined,
      notes,
      transactionSource: 'manual'
    };

    await this.createInventoryTransaction(tenantId, transactionData);
  }

  async removeInventoryStock(
    tenantId: string, 
    itemId: string, 
    quantity: number, 
    reason: string, 
    performedBy: string,
    notes?: string
  ): Promise<void> {
    // Get current item
    const item = await this.getById<InventoryItem>(tenantId, 'inventory', itemId);
    if (!item) throw new Error('Inventory item not found');

    // Check if sufficient stock
    if (item.currentStock < quantity) {
      throw new Error('Insufficient stock');
    }

    // Update stock
    const newStock = item.currentStock - quantity;
    await this.updateInventoryStock(tenantId, itemId, newStock);

    // Create transaction record
    const transactionData: Omit<InventoryTransaction, 'id' | 'createdAt' | 'updatedAt'> = {
      itemId,
      itemName: item.name,
      type: 'out',
      quantity,
      unit: item.unit,
      reason,
      performedBy,
      notes,
      transactionSource: 'manual'
    };

    await this.createInventoryTransaction(tenantId, transactionData);
  }

  // Customer management methods
  async createCustomer(tenantId: string, customerData: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    return this.create<Customer>(tenantId, 'customers', customerData);
  }

  async updateTenant(tenantId: string, updateData: Partial<{
    businessName: string;
    name: string;
    email: string;
    phone: string;
    address: string;
    instagramHandle: string;
    isQrOrderingEnabled: boolean;
    posSimplified: boolean;
  }>): Promise<void> {
    try {
      const tenantRef = doc(db, 'tenants', tenantId);
      const cleanData = Object.entries(updateData).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      await updateDoc(tenantRef, {
        ...cleanData,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating tenant:', error);
      throw error;
    }
  }

  async updateQrOrderingStatus(tenantId: string, isQrOrderingEnabled: boolean): Promise<void> {
    try {
      const tenantRef = doc(db, 'tenants', tenantId);
      await updateDoc(tenantRef, {
        isQrOrderingEnabled,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating QR ordering status:', error);
      throw error;
    }
  }

  async getCustomers(tenantId: string): Promise<Customer[]> {
    return this.getAll<Customer>(tenantId, 'customers', 'name', 'asc');
  }

  async getCustomerByName(tenantId: string, name: string): Promise<Customer | null> {
    const customers = await this.getByField<Customer>(tenantId, 'customers', 'name', name);
    return customers.length > 0 ? customers[0] : null;
  }

  async updateCustomer(tenantId: string, customerId: string, customerData: Partial<Omit<Customer, 'id' | 'createdAt'>>): Promise<void> {
    return this.update<Customer>(tenantId, 'customers', customerId, customerData);
  }

  async updateCustomerStats(tenantId: string, customerName: string): Promise<void> {
    // Get customer
    const customer = await this.getCustomerByName(tenantId, customerName);
    if (!customer) return;

    // Calculate stats from orders
    const orders = await this.getByField<Order>(tenantId, 'orders', 'customer', customerName);
    
    // Calculate total spent from order totals (not just payments)
    const totalSpent = orders.reduce((sum, order) => sum + (order.total || 0), 0);
    const totalOrders = orders.length;
    const lastOrderDate = orders.length > 0 ? 
      new Date(Math.max(...orders.map(o => new Date(o.createdAt!).getTime()))) : undefined;
    
    // Determine status (active if ordered in last 30 days)
    const daysSinceLastOrder = lastOrderDate ? 
      (new Date().getTime() - lastOrderDate.getTime()) / (1000 * 60 * 60 * 24) : Infinity;
    const status: 'active' | 'inactive' = daysSinceLastOrder <= 30 ? 'active' : 'inactive';

    // Update customer
    await this.updateCustomer(tenantId, customer.id!, {
      totalOrders,
      totalSpent,
      lastOrderDate,
      status
    });
  }

  async getOrCreateCustomer(tenantId: string, name: string, phone?: string): Promise<Customer> {
    // Try to find existing customer
    let customer = await this.getCustomerByName(tenantId, name);
    
    if (!customer) {
      // Create new customer
      const customerData: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'> = {
        name,
        phone,
        status: 'active',
        totalOrders: 0,
        totalSpent: 0,
        dateJoined: new Date()
      };
      
      const customerId = await this.createCustomer(tenantId, customerData);
      customer = await this.getById<Customer>(tenantId, 'customers', customerId);
    } else if (phone && !customer.phone) {
      // Update phone if not set
      await this.updateCustomer(tenantId, customer.id!, { phone });
      customer.phone = phone;
    }
    
    return customer!;
  }

  // Unit conversion utility methods
  convertToBaseUnits(rawMaterial: RawMaterial, quantity: number, fromUnit: string): number {
    if (fromUnit === rawMaterial.baseUnit) {
      return quantity;
    }
    
    const conversion = rawMaterial.unitConversions.find(uc => uc.unitName === fromUnit);
    if (!conversion) {
      throw new Error(`Unit conversion not found for ${fromUnit}`);
    }
    
    return quantity * conversion.conversionFactor;
  }

  convertFromBaseUnits(rawMaterial: RawMaterial, baseQuantity: number, toUnit: string): number {
    if (toUnit === rawMaterial.baseUnit) {
      return baseQuantity;
    }
    
    const conversion = rawMaterial.unitConversions.find(uc => uc.unitName === toUnit);
    if (!conversion) {
      throw new Error(`Unit conversion not found for ${toUnit}`);
    }
    
    return baseQuantity / conversion.conversionFactor;
  }

  getAvailableUnitsForPurchase(rawMaterial: RawMaterial): string[] {
    const units = [rawMaterial.baseUnit];
    rawMaterial.unitConversions
      .filter(uc => uc.isPurchaseUnit)
      .forEach(uc => units.push(uc.unitName));
    return units;
  }

  getAvailableUnitsForSale(rawMaterial: RawMaterial): string[] {
    const units = [rawMaterial.baseUnit];
    rawMaterial.unitConversions
      .filter(uc => uc.isSaleUnit)
      .forEach(uc => units.push(uc.unitName));
    return units;
  }

  calculateCostPerUnit(rawMaterial: RawMaterial, unit: string): number {
    if (unit === rawMaterial.baseUnit) {
      return rawMaterial.costPerUnit;
    }
    
    const conversion = rawMaterial.unitConversions.find(uc => uc.unitName === unit);
    if (!conversion) {
      throw new Error(`Unit conversion not found for ${unit}`);
    }
    
    // If specific cost per conversion unit is set, use it
    if (conversion.costPerConversionUnit) {
      return conversion.costPerConversionUnit;
    }
    
    // Otherwise calculate based on base unit cost
    return rawMaterial.costPerUnit * conversion.conversionFactor;
  }

  // Enhanced inventory stock methods with unit conversion
  async addRawMaterialStock(
    tenantId: string, 
    materialId: string, 
    quantity: number, 
    unit: string,
    reason: string, 
    performedBy: string,
    supplier?: string,
    costPerUnit?: number,
    notes?: string
  ): Promise<void> {
    // Get current material
    const material = await this.getById<RawMaterial>(tenantId, 'rawMaterials', materialId);
    if (!material) throw new Error('Raw material not found');

    // Convert quantity to base units
    const baseQuantity = this.convertToBaseUnits(material, quantity, unit);

    // Update stock in base units
    const newStock = material.currentStock + baseQuantity;
    await this.update<RawMaterial>(tenantId, 'rawMaterials', materialId, { 
      currentStock: newStock,
      lastRestocked: new Date()
    });

    // Create transaction record
    const transactionData: Omit<InventoryTransaction, 'id' | 'createdAt' | 'updatedAt'> = {
      itemId: materialId,
      itemName: material.name,
      type: 'in',
      quantity: baseQuantity, // Store in base units
      unit: material.baseUnit, // Always store transactions in base units
      reason,
      performedBy,
      supplier,
      costPerUnit: costPerUnit ? costPerUnit / (unit === material.baseUnit ? 1 : 
        material.unitConversions.find(uc => uc.unitName === unit)?.conversionFactor || 1) : undefined,
      totalCost: costPerUnit ? costPerUnit * quantity : undefined,
      notes: `${quantity} ${unit} added${notes ? ` - ${notes}` : ''}`,
      transactionSource: 'manual'
    };

    await this.createInventoryTransaction(tenantId, transactionData);
  }

  async removeRawMaterialStock(
    tenantId: string, 
    materialId: string, 
    quantity: number, 
    unit: string,
    reason: string, 
    performedBy: string,
    notes?: string
  ): Promise<void> {
    // Get current material
    const material = await this.getById<RawMaterial>(tenantId, 'rawMaterials', materialId);
    if (!material) throw new Error('Raw material not found');

    // Convert quantity to base units
    const baseQuantity = this.convertToBaseUnits(material, quantity, unit);

    // Check if sufficient stock
    if (material.currentStock < baseQuantity) {
      const availableInRequestedUnit = this.convertFromBaseUnits(material, material.currentStock, unit);
      throw new Error(`Insufficient stock. Available: ${availableInRequestedUnit.toFixed(2)} ${unit}`);
    }

    // Update stock in base units
    const newStock = material.currentStock - baseQuantity;
    await this.update<RawMaterial>(tenantId, 'rawMaterials', materialId, { 
      currentStock: newStock
    });

    // Create transaction record
    const transactionData: Omit<InventoryTransaction, 'id' | 'createdAt' | 'updatedAt'> = {
      itemId: materialId,
      itemName: material.name,
      type: 'out',
      quantity: baseQuantity, // Store in base units
      unit: material.baseUnit, // Always store transactions in base units
      reason,
      performedBy,
      notes: `${quantity} ${unit} removed${notes ? ` - ${notes}` : ''}`,
      transactionSource: 'manual'
    };

    await this.createInventoryTransaction(tenantId, transactionData);
  }

  // New method: Process menu item sale and deduct ingredients
  async processMenuItemSale(
    tenantId: string,
    menuItemId: string,
    quantity: number,
    orderId: string,
    performedBy: string = 'System'
  ): Promise<{ success: boolean; errors: string[] }> {
    const errors: string[] = [];
    
    try {
      // Get menu item with ingredients
      const menuItem = await this.getById<MenuItem>(tenantId, 'menuItems', menuItemId);
      if (!menuItem) {
        errors.push('Menu item not found');
        return { success: false, errors };
      }

      // Skip if no ingredients or auto-deduction disabled
      if (!menuItem.ingredients || menuItem.ingredients.length === 0 || !menuItem.autoDeductInventory) {
        return { success: true, errors: [] };
      }

      // Process each ingredient
      for (const ingredient of menuItem.ingredients) {
        try {
          const totalQuantityNeeded = ingredient.quantity * quantity;
          
          // Get the raw material to create proper transaction
          const material = await this.getById<RawMaterial>(tenantId, 'rawMaterials', ingredient.rawMaterialId);
          if (!material) {
            errors.push(`Raw material not found: ${ingredient.rawMaterialId}`);
            continue;
          }

          // Convert quantity to base units
          const baseQuantity = this.convertToBaseUnits(material, totalQuantityNeeded, ingredient.unit);

          // Check if sufficient stock
          if (material.currentStock < baseQuantity) {
            const availableInRequestedUnit = this.convertFromBaseUnits(material, material.currentStock, ingredient.unit);
            errors.push(`${material.name}: Insufficient stock. Available: ${availableInRequestedUnit.toFixed(2)} ${ingredient.unit}, Required: ${totalQuantityNeeded}`);
            continue;
          }

          // Update stock in base units
          const newStock = material.currentStock - baseQuantity;
          await this.update<RawMaterial>(tenantId, 'rawMaterials', ingredient.rawMaterialId, { 
            currentStock: newStock
          });

          // Create sale transaction record
          const transactionData: Omit<InventoryTransaction, 'id' | 'createdAt' | 'updatedAt'> = {
            itemId: ingredient.rawMaterialId,
            itemName: material.name,
            type: 'out',
            quantity: baseQuantity, // Store in base units
            unit: material.baseUnit, // Always store transactions in base units
            reason: 'Sale - Menu Item',
            performedBy,
            notes: `Order #${orderId} - ${menuItem.name} (${quantity}x) - Used ${totalQuantityNeeded} ${ingredient.unit}`,
            transactionSource: 'sale',
            orderId,
            menuItemId
          };

          await this.createInventoryTransaction(tenantId, transactionData);
        } catch (error) {
          const material = await this.getById<RawMaterial>(tenantId, 'rawMaterials', ingredient.rawMaterialId);
          const materialName = material?.name || 'Unknown Material';
          errors.push(`${materialName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      return { 
        success: errors.length === 0, 
        errors 
      };
    } catch (error) {
      console.error('Error processing menu item sale:', error);
      errors.push('Failed to process inventory deduction');
      return { success: false, errors };
    }
  }

  // Helper method to create cigarette products with sub-unit system
  async createCigaretteProduct(
    tenantId: string,
    name: string,
    supplier: string,
    costPerBox: number,
    piecesPerBox: number = 20,
    location?: string
  ): Promise<string> {
    const costPerPiece = costPerBox / piecesPerBox;
    
    const cigaretteData: Omit<RawMaterial, 'id' | 'createdAt' | 'updatedAt'> = {
      name,
      category: 'Cigarettes',
      currentStock: 0, // Will be in pieces (sub-units)
      minStock: 100, // Minimum 100 pieces
      maxStock: 2000, // Maximum 2000 pieces
      
      // Main unit system (purchase unit)
      mainUnit: 'box',
      mainUnitQuantity: 0, // Will be calculated from currentStock
      
      // Sub-unit system (sale unit)
      subUnit: 'piece',
      subUnitsPerMainUnit: piecesPerBox,
      
      // Cost information
      costPerMainUnit: costPerBox,
      costPerSubUnit: costPerPiece,
      
      // Supplier and location
      supplier,
      location: location || 'Cigarette Storage',
      description: `${name} cigarettes - ${piecesPerBox} pieces per box`,
      
      // Legacy fields for backward compatibility
      baseUnit: 'piece',
      costPerUnit: costPerPiece,
      unitConversions: [
        {
          unitName: 'box',
          conversionFactor: piecesPerBox,
          isPurchaseUnit: true,
          isSaleUnit: true,
          costPerConversionUnit: costPerBox
        }
      ],
      defaultPurchaseUnit: 'box',
      defaultSaleUnit: 'piece'
    };

    return this.createRawMaterial(tenantId, cigaretteData);
  }

  // Helper method to create cigarette menu items
  async createCigaretteMenuItem(
    tenantId: string,
    cigaretteRawMaterialId: string,
    cigaretteName: string,
    saleUnit: 'piece' | 'box of 10' | 'box of 20',
    markupPercentage: number = 50 // 50% markup by default
  ): Promise<string> {
    // Get the raw material to calculate pricing
    const rawMaterial = await this.getById<RawMaterial>(tenantId, 'rawMaterials', cigaretteRawMaterialId);
    if (!rawMaterial) throw new Error('Cigarette raw material not found');

    // Calculate cost per sale unit
    const costPerSaleUnit = this.calculateCostPerUnit(rawMaterial, saleUnit);
    const salePrice = costPerSaleUnit * (1 + markupPercentage / 100);

    // Determine quantity needed from raw material
    const saleUnitConversion = saleUnit === 'piece' ? 1 : 
      rawMaterial.unitConversions.find(uc => uc.unitName === saleUnit)?.conversionFactor || 1;

    const menuItemData: Omit<MenuItem, 'id' | 'createdAt' | 'updatedAt'> = {
      name: `${cigaretteName}${saleUnit !== 'piece' ? ` (${saleUnit})` : ''}`,
      category: 'Cigarettes',
      price: salePrice,
      description: `${cigaretteName} cigarette${saleUnit !== 'piece' ? ` sold as ${saleUnit}` : ''}`,
      ingredients: [
        {
          rawMaterialId: cigaretteRawMaterialId,
          quantity: saleUnitConversion, // How many pieces needed
          unit: rawMaterial.baseUnit // Always use base unit for ingredients
        }
      ],
      preparationTime: 0, // No preparation needed
      isAvailable: true,
      autoDeductInventory: true,
      saleUnit,
      saleUnitConversion
    };

    return this.createMenuItem(tenantId, menuItemData);
  }

  // Real-time subscription methods
  
  /**
   * Subscribe to real-time updates for a collection
   */
  subscribeToCollection<T extends BaseDocument>(
    tenantId: string,
    collectionName: string,
    callback: (data: T[]) => void,
    orderByField: string = 'createdAt',
    orderDirection: 'asc' | 'desc' = 'desc'
  ): Unsubscribe {
    try {
      const collectionRef = getTenantCollection(tenantId, collectionName);
      const q = query(collectionRef, orderBy(orderByField, orderDirection));
      
      return onSnapshot(q, (snapshot) => {
        const data: T[] = [];
        snapshot.forEach((doc) => {
          const docData = doc.data();
          data.push({
            id: doc.id,
            ...docData,
            createdAt: docData.createdAt?.toDate(),
            updatedAt: docData.updatedAt?.toDate()
          } as T);
        });
        callback(data);
      }, (error) => {
        console.error(`Error in ${collectionName} subscription:`, error);
      });
    } catch (error) {
      console.error(`Error setting up ${collectionName} subscription:`, error);
      return () => {}; // Return empty unsubscribe function
    }
  }

  /**
   * Subscribe to real-time orders updates
   */
  subscribeToOrders(tenantId: string, callback: (orders: Order[]) => void): Unsubscribe {
    return this.subscribeToCollection<Order>(tenantId, 'orders', callback);
  }

  /**
   * Subscribe to real-time payments updates
   */
  subscribeToPayments(tenantId: string, callback: (payments: Payment[]) => void): Unsubscribe {
    return this.subscribeToCollection<Payment>(tenantId, 'payments', callback);
  }

  /**
   * Subscribe to real-time raw materials updates
   */
  subscribeToRawMaterials(tenantId: string, callback: (materials: RawMaterial[]) => void): Unsubscribe {
    return this.subscribeToCollection<RawMaterial>(tenantId, 'rawMaterials', callback);
  }

  /**
   * Subscribe to real-time customers updates
   */
  subscribeToCustomers(tenantId: string, callback: (customers: Customer[]) => void): Unsubscribe {
    return this.subscribeToCollection<Customer>(tenantId, 'customers', callback);
  }

  /**
   * Subscribe to real-time menu items updates
   */
  subscribeToMenuItems(tenantId: string, callback: (menuItems: MenuItem[]) => void): Unsubscribe {
    return this.subscribeToCollection<MenuItem>(tenantId, 'menuItems', callback);
  }

  /**
   * Subscribe to real-time inventory transactions updates
   */
  subscribeToInventoryTransactions(tenantId: string, callback: (transactions: InventoryTransaction[]) => void): Unsubscribe {
    return this.subscribeToCollection<InventoryTransaction>(tenantId, 'inventoryTransactions', callback);
  }

  /**
   * Subscribe to multiple collections at once for dashboard
   */
  subscribeToDashboardData(
    tenantId: string,
    callbacks: {
      onOrdersUpdate: (orders: Order[]) => void;
      onPaymentsUpdate: (payments: Payment[]) => void;
      onRawMaterialsUpdate: (materials: RawMaterial[]) => void;
      onCustomersUpdate: (customers: Customer[]) => void;
    }
  ): () => void {
    const unsubscribeOrders = this.subscribeToOrders(tenantId, callbacks.onOrdersUpdate);
    const unsubscribePayments = this.subscribeToPayments(tenantId, callbacks.onPaymentsUpdate);
    const unsubscribeRawMaterials = this.subscribeToRawMaterials(tenantId, callbacks.onRawMaterialsUpdate);
    const unsubscribeCustomers = this.subscribeToCustomers(tenantId, callbacks.onCustomersUpdate);

    // Return a function that unsubscribes from all
    return () => {
      unsubscribeOrders();
      unsubscribePayments();
      unsubscribeRawMaterials();
      unsubscribeCustomers();
    };
  }

  // Debug method to help troubleshoot order issues
  async debugCustomerOrders(tenantId: string, customerName: string): Promise<void> {
    console.log(`🔍 DEBUG: Analyzing orders for customer: ${customerName}`);
    
    const orders = await this.getByField<Order>(tenantId, 'orders', 'customer', customerName);
    const payments = await this.getPayments(tenantId);
    
    console.log(`Found ${orders.length} orders for ${customerName}`);
    
    orders.forEach((order, index) => {
      const orderPayments = payments.filter(p => p.orderId === order.id);
      const totalPayments = orderPayments.reduce((sum, p) => sum + p.amount, 0);
      
      console.log(`Order ${index + 1} (${order.id}):`);
      console.log(`  Status: ${order.status}`);
      console.log(`  Is Paid: ${order.isPaid}`);
      console.log(`  Total: ₹${order.total}`);
      console.log(`  Paid Amount: ₹${order.paidAmount}`);
      console.log(`  Remaining: ₹${order.remainingAmount}`);
      console.log(`  Items: ${order.items?.length || 0}`);
      console.log(`  Payments in DB: ${orderPayments.length} totaling ₹${totalPayments}`);
      console.log(`  Created: ${order.createdAt}`);
      console.log(`  Should be active: ${order.status === 'active' && !order.isPaid && order.remainingAmount > 0}`);
      console.log('---');
    });
  }

  // Category Management Methods
  async createCategory(tenantId: string, categoryData: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    return this.create<Category>(tenantId, 'categories', categoryData);
  }

  async getCategories(tenantId: string, type?: 'menuItem' | 'rawMaterial'): Promise<Category[]> {
    if (type) {
      return this.getByField<Category>(tenantId, 'categories', 'type', type);
    }
    return this.getAll<Category>(tenantId, 'categories', 'name', 'asc');
  }

  async updateCategory(tenantId: string, categoryId: string, categoryData: Partial<Omit<Category, 'id' | 'createdAt'>>): Promise<void> {
    return this.update<Category>(tenantId, 'categories', categoryId, categoryData);
  }

  async deleteCategory(tenantId: string, categoryId: string): Promise<void> {
    // Check if category is marked as default
    const category = await this.getById<Category>(tenantId, 'categories', categoryId);
    if (category?.isDefault) {
      throw new Error('Cannot delete default category');
    }

    // Check if category is being used by any menu items or raw materials
    const menuItems = await this.getByField<MenuItem>(tenantId, 'menuItems', 'category', category?.name || '');
    const rawMaterials = await this.getByField<RawMaterial>(tenantId, 'rawMaterials', 'category', category?.name || '');

    if (menuItems.length > 0 || rawMaterials.length > 0) {
      throw new Error('Cannot delete category that is currently in use');
    }

    return this.delete(tenantId, 'categories', categoryId);
  }

  async getCategoryByName(tenantId: string, name: string, type: 'menuItem' | 'rawMaterial'): Promise<Category | null> {
    const categories = await this.getByField<Category>(tenantId, 'categories', 'name', name);
    return categories.find(cat => cat.type === type) || null;
  }

  async initializeDefaultCategories(tenantId: string): Promise<void> {
    const existingCategories = await this.getCategories(tenantId);
    
    // Default menu item categories
    const defaultMenuCategories = [
      { name: 'Coffee', type: 'menuItem' as const, description: 'Coffee-based beverages', isDefault: true },
      { name: 'Tea', type: 'menuItem' as const, description: 'Tea-based beverages', isDefault: true },
      { name: 'Pastries', type: 'menuItem' as const, description: 'Baked goods and pastries', isDefault: true },
      { name: 'Sandwiches', type: 'menuItem' as const, description: 'Sandwiches and wraps', isDefault: true },
      { name: 'Salads', type: 'menuItem' as const, description: 'Fresh salads', isDefault: true },
      { name: 'Desserts', type: 'menuItem' as const, description: 'Sweet desserts', isDefault: true },
      { name: 'Beverages', type: 'menuItem' as const, description: 'Other beverages', isDefault: true },
      { name: 'Cigarettes', type: 'menuItem' as const, description: 'Tobacco products', isDefault: true }
    ];

    // Default raw material categories
    const defaultRawMaterialCategories = [
      { name: 'Coffee', type: 'rawMaterial' as const, description: 'Coffee beans and related', isDefault: true },
      { name: 'Dairy', type: 'rawMaterial' as const, description: 'Milk and dairy products', isDefault: true },
      { name: 'Sweeteners', type: 'rawMaterial' as const, description: 'Sugar and sweetening agents', isDefault: true },
      { name: 'Pastries', type: 'rawMaterial' as const, description: 'Baking ingredients', isDefault: true },
      { name: 'Supplies', type: 'rawMaterial' as const, description: 'General supplies', isDefault: true },
      { name: 'Packaging', type: 'rawMaterial' as const, description: 'Packaging materials', isDefault: true },
      { name: 'Cleaning', type: 'rawMaterial' as const, description: 'Cleaning supplies', isDefault: true },
      { name: 'Cigarettes', type: 'rawMaterial' as const, description: 'Tobacco products', isDefault: true }
    ];

    const allDefaultCategories = [...defaultMenuCategories, ...defaultRawMaterialCategories];

    // Only create categories that don't already exist
    for (const defaultCategory of allDefaultCategories) {
      const exists = existingCategories.find(cat => 
        cat.name === defaultCategory.name && cat.type === defaultCategory.type
      );
      
      if (!exists) {
        await this.createCategory(tenantId, defaultCategory);
      }
    }
  }

  // Table management methods
  async getTables(tenantId: string): Promise<Table[]> {
    return this.getAll<Table>(tenantId, 'tables', 'tableNumber', 'asc');
  }

  async createTable(tenantId: string, tableData: Omit<Table, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      // Generate sequential table ID
      const nextTableId = await this.generateNextTableId(tenantId);
      
      const collectionRef = getTenantCollection(tenantId, 'tables');
      
      // Filter out undefined values
      const cleanData = Object.entries(tableData).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);
      
      const docData = {
        ...cleanData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      // Create document with custom ID
      const docRef = doc(collectionRef, nextTableId);
      await setDoc(docRef, docData);
      
      // The id field will be automatically set by the getAll method when retrieving
      // We don't need to store it in the document data since Firestore uses the document ID
      
      return nextTableId;
    } catch (error) {
      console.error('Error creating table:', error);
      throw error;
    }
  }

  /**
   * Generate the next sequential table ID in format TB-0001, TB-0002, etc.
   */
  private async generateNextTableId(tenantId: string): Promise<string> {
    try {
      const tables = await this.getTables(tenantId);
      
      // Find the highest existing table ID number
      let maxNumber = 0;
      tables.forEach(table => {
        if (table.id && table.id.startsWith('TB-')) {
          const numberPart = table.id.substring(3); // Remove 'TB-' prefix
          const number = parseInt(numberPart, 10);
          if (!isNaN(number) && number > maxNumber) {
            maxNumber = number;
          }
        }
      });
      
      // Generate next sequential number
      const nextNumber = maxNumber + 1;
      const nextTableId = `TB-${nextNumber.toString().padStart(4, '0')}`;
      
      return nextTableId;
    } catch (error) {
      console.error('Error generating table ID:', error);
      // Fallback to timestamp-based ID if something goes wrong
      return `TB-${Date.now().toString().slice(-4)}`;
    }
  }

  async updateTable(tenantId: string, tableId: string, updates: Partial<Table>): Promise<void> {
    return this.update<Table>(tenantId, 'tables', tableId, updates);
  }

  async deleteTable(tenantId: string, tableId: string): Promise<void> {
    return this.delete(tenantId, 'tables', tableId);
  }

  async getTableByNumber(tenantId: string, tableNumber: string): Promise<Table | null> {
    const tables = await this.getByField<Table>(tenantId, 'tables', 'tableNumber', tableNumber);
    return tables.length > 0 ? tables[0] : null;
  }

  /**
   * Get table with current order information
   */
  async getTableWithOrderInfo(tenantId: string, tableId: string): Promise<Table & { currentOrder?: Order } | null> {
    try {
      const table = await this.getById<Table>(tenantId, 'tables', tableId);
      if (!table) return null;
      
      // If table has a current order, fetch it
      if (table.currentOrderId) {
        const order = await this.getById<Order>(tenantId, 'orders', table.currentOrderId);
        return {
          ...table,
          currentOrder: order || undefined
        };
      }
      
      return table;
    } catch (error) {
      console.error('Error getting table with order info:', error);
      return null;
    }
  }

  /**
   * Get all tables with their current order status
   */
  async getTablesWithOrderStatus(tenantId: string): Promise<Table[]> {
    try {
      const tables = await this.getTables(tenantId);
      
      // For each table, check if it has an active order
      const tablesWithStatus = await Promise.all(
        tables.map(async (table) => {
          if (table.currentOrderId) {
            const order = await this.getById<Order>(tenantId, 'orders', table.currentOrderId);
            if (order && order.status === 'active') {
              return {
                ...table,
                orderStatus: 'occupied' as const
              };
            } else {
              // Order might be completed/cancelled, clear the currentOrderId
              await this.updateTable(tenantId, table.id!, {
                currentOrderId: undefined,
                orderStatus: 'vacant'
              });
              return {
                ...table,
                currentOrderId: undefined,
                orderStatus: 'vacant' as const
              };
            }
          }
          
          return {
            ...table,
            orderStatus: 'vacant' as const
          };
        })
      );
      
      return tablesWithStatus;
    } catch (error) {
      console.error('Error getting tables with order status:', error);
      return [];
    }
  }

  // ==================== INVITATION MANAGEMENT ====================

  /**
   * Generate a secure random token for invitations
   */
  private generateInvitationToken(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let token = '';
    for (let i = 0; i < 32; i++) {
      token += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return token;
  }

  /**
   * Create a new invitation
   */
  async createInvitation(
    tenantId: string, 
    createdBy: string,
    assignedFeatures: string[],
    expirationDays: number = 7,
    workerName?: string,
    workerEmail?: string
  ): Promise<string> {
    const token = this.generateInvitationToken();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + expirationDays);

    const invitationData: Omit<Invitation, 'id' | 'createdAt' | 'updatedAt'> = {
      token,
      createdBy,
      assignedFeatures,
      expiresAt,
      status: 'active',
      workerName,
      workerEmail
    };

    return await this.create<Invitation>(tenantId, 'invitations', invitationData);
  }

  /**
   * Get invitation by token
   */
  async getInvitationByToken(tenantId: string, token: string): Promise<Invitation | null> {
    const invitations = await this.getByField<Invitation>(tenantId, 'invitations', 'token', token);
    return invitations.length > 0 ? invitations[0] : null;
  }

  /**
   * Get all invitations for a tenant
   */
  async getInvitations(tenantId: string): Promise<Invitation[]> {
    return await this.getAll<Invitation>(tenantId, 'invitations', 'createdAt', 'desc');
  }

  /**
   * Get active invitations (not used or expired)
   */
  async getActiveInvitations(tenantId: string): Promise<Invitation[]> {
    const invitations = await this.getInvitations(tenantId);
    const now = new Date();
    
    return invitations.filter(invitation => 
      invitation.status === 'active' && 
      invitation.expiresAt > now
    );
  }

  /**
   * Use an invitation (mark as used)
   */
  async useInvitation(tenantId: string, invitationId: string, usedBy: string): Promise<void> {
    await this.update<Invitation>(tenantId, 'invitations', invitationId, {
      status: 'used',
      usedAt: new Date(),
      usedBy
    });
  }

  /**
   * Revoke an invitation
   */
  async revokeInvitation(tenantId: string, invitationId: string): Promise<void> {
    await this.update<Invitation>(tenantId, 'invitations', invitationId, {
      status: 'expired'
    });
  }

  /**
   * Clean up expired invitations
   */
  async cleanupExpiredInvitations(tenantId: string): Promise<void> {
    const invitations = await this.getInvitations(tenantId);
    const now = new Date();
    
    const expiredInvitations = invitations.filter(invitation => 
      invitation.status === 'active' && 
      invitation.expiresAt <= now
    );

    for (const invitation of expiredInvitations) {
      await this.update<Invitation>(tenantId, 'invitations', invitation.id!, {
        status: 'expired'
      });
    }
  }

  // ==================== ROLE MANAGEMENT ====================

  /**
   * Create a new role
   */
  async createRole(tenantId: string, roleData: Omit<Role, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    return await this.create<Role>(tenantId, 'roles', roleData);
  }

  /**
   * Get all roles for a tenant
   */
  async getRoles(tenantId: string): Promise<Role[]> {
    return await this.getAll<Role>(tenantId, 'roles', 'name', 'asc');
  }

  /**
   * Update role permissions
   */
  async updateRole(tenantId: string, roleId: string, roleData: Partial<Omit<Role, 'id' | 'createdAt'>>): Promise<void> {
    await this.update<Role>(tenantId, 'roles', roleId, roleData);
  }

  /**
   * Delete a role
   */
  async deleteRole(tenantId: string, roleId: string): Promise<void> {
    await this.delete(tenantId, 'roles', roleId);
  }

  /**
   * Get role by name
   */
  async getRoleByName(tenantId: string, name: string): Promise<Role | null> {
    const roles = await this.getByField<Role>(tenantId, 'roles', 'name', name);
    return roles.length > 0 ? roles[0] : null;
  }

  /**
   * Initialize default roles for a tenant
   */
  async initializeDefaultRoles(tenantId: string): Promise<void> {
    const existingRoles = await this.getRoles(tenantId);
    
    // Create default worker role if it doesn't exist
    const workerRoleExists = existingRoles.some(role => role.name === 'worker');
    if (!workerRoleExists) {
      await this.createRole(tenantId, {
        name: 'worker',
        permissions: [
          'DASHBOARD_LIMITED',
          'ORDERS_VIEW',
          'ORDERS_UPDATE'
        ],
        isDefault: true,
        description: 'Default role for workers with basic permissions'
      });
    }

    // Create default owner role if it doesn't exist
    const ownerRoleExists = existingRoles.some(role => role.name === 'owner');
    if (!ownerRoleExists) {
      await this.createRole(tenantId, {
        name: 'owner',
        permissions: [], // Owners get all permissions by default in the logic
        isDefault: true,
        description: 'Business owner with full access to all features'
      });
    }
  }

  // ==================== USER MANAGEMENT ====================

  /**
   * Get all users for a tenant (from the users subcollection)
   */
  async getTenantUsers(tenantId: string): Promise<any[]> {
    return await this.getAll(tenantId, 'users', 'createdAt', 'desc');
  }

  /**
   * Update user permissions
   */
  async updateUserPermissions(tenantId: string, userId: string, permissions: string[]): Promise<void> {
    const collection = getTenantCollection(tenantId, 'users');
    const userDoc = doc(collection, userId);
    await updateDoc(userDoc, {
      permissions,
      updatedAt: new Date()
    });
  }

  /**
   * Get user by ID from tenant's users subcollection
   */
  async getTenantUser(tenantId: string, userId: string): Promise<any | null> {
    return await this.getById(tenantId, 'users', userId);
  }

  /**
   * Remove user from tenant (delete from users subcollection)
   */
  async removeUserFromTenant(tenantId: string, userId: string): Promise<void> {
    await this.delete(tenantId, 'users', userId);
  }

  async findUserByPhone(phone: string): Promise<any | null> {
    try {
      // Search in all tenants for a user with the given phone number
      const tenantsRef = collection(db, 'tenants');
      const tenantsSnapshot = await getDocs(tenantsRef);
      
      for (const tenantDoc of tenantsSnapshot.docs) {
        const tenantId = tenantDoc.id;
        const usersRef = collection(db, 'tenants', tenantId, 'users');
        const usersSnapshot = await getDocs(usersRef);
        
        for (const userDoc of usersSnapshot.docs) {
          const userData = userDoc.data() as any;
          if (userData.phone === phone) {
            return {
              userId: userDoc.id,
              tenantId,
              userData: { id: userDoc.id, ...userData }
            };
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error finding user by phone:', error);
      throw error;
    }
  }

  async addMultipleItemsToOrder(tenantId: string, orderId: string, items: OrderItem[]): Promise<void> {
    console.log(`📦 Adding ${items.length} items to order ${orderId} in batch`);
    console.log(`🔍 Items to add:`, items);
    
    const order = await this.getById<Order>(tenantId, 'orders', orderId);
    if (!order) throw new Error('Order not found');
    
    console.log(`🔍 Retrieved order data:`, order);

    const updatedItems = [...(order.items || []), ...items];
    console.log(`🔍 Updated items:`, updatedItems);
    
    // Validate that all items have required fields and no undefined values
    const validatedItems = updatedItems.map(item => {
      const cleanItem = Object.entries(item).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);
      
      // Ensure required fields are present
      if (!cleanItem.id || !cleanItem.menuItemId || !cleanItem.menuItemName || 
          typeof cleanItem.quantity !== 'number' || typeof cleanItem.totalPrice !== 'number') {
        throw new Error(`Invalid order item: missing required fields or invalid values`);
      }
      
      return cleanItem;
    });
    
    console.log(`🔍 Validated items:`, validatedItems);
    
    const subtotal = validatedItems.reduce((sum, item) => sum + item.totalPrice, 0);
    const total = subtotal; // No tax
    const remainingAmount = total - (order.paidAmount || 0);

    const updateData: Partial<Order> = {
      items: validatedItems,
      subtotal,
      tax: 0, // No tax
      total,
      remainingAmount
    };

    // Ensure no undefined values are passed to Firebase
    const cleanUpdateData = Object.entries(updateData).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    console.log(`💾 Updating order ${orderId} with ${items.length} new items`);
    console.log(`🔍 Clean update data:`, cleanUpdateData);
    await this.update<Order>(tenantId, 'orders', orderId, cleanUpdateData);

    // Update customer stats after adding items
    await this.updateCustomerStats(tenantId, order.customer);

    // Process inventory deduction for all items in parallel
    console.log(`🏭 Processing inventory deductions for ${items.length} items`);
    const inventoryPromises = items.map(async (item) => {
      try {
        const result = await this.processMenuItemSale(
          tenantId,
          item.menuItemId,
          item.quantity,
          orderId,
          'Order System'
        );

        if (!result.success && result.errors.length > 0) {
          console.warn(`Inventory deduction warnings for ${item.menuItemName}:`, result.errors);
        }
      } catch (error) {
        console.error(`Failed to process inventory deduction for ${item.menuItemName}:`, error);
        // Continue with other items even if one fails
      }
    });

    // Wait for all inventory updates to complete
    await Promise.allSettled(inventoryPromises);
    console.log(`✅ Batch order update completed for ${orderId}`);
  }

  /**
   * Get today's orders for a tenant (local time)
   */
  async getTodayOrders(tenantId: string): Promise<Order[]> {
    const allOrders = await this.getOrders(tenantId);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return allOrders.filter(order => {
      if (!order.createdAt) return false;
      const orderDate = new Date(order.createdAt);
      return orderDate >= today && orderDate < tomorrow;
    });
  }

  /**
   * Get today's payments for a tenant (local time)
   */
  async getTodayPayments(tenantId: string): Promise<Payment[]> {
    const allPayments = await this.getPayments(tenantId);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return allPayments.filter(payment => {
      if (!payment.createdAt) return false;
      const paymentDate = new Date(payment.createdAt);
      return paymentDate >= today && paymentDate < tomorrow;
    });
  }

  /**
   * Get today's inventory transactions for a tenant (local time)
   */
  async getTodayInventoryTransactions(tenantId: string): Promise<InventoryTransaction[]> {
    const allTransactions = await this.getInventoryTransactions(tenantId);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return allTransactions.filter(tx => {
      if (!tx.createdAt) return false;
      const txDate = new Date(tx.createdAt);
      return txDate >= today && txDate < tomorrow;
    });
  }

  // ==================== CHAT MANAGEMENT ====================

  /**
   * Create a new chat session
   */
  async createChatSession(tenantId: string, sessionData: Omit<ChatSession, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      return await this.create(tenantId, 'chatSessions', sessionData);
    } catch (error) {
      console.error('Error creating chat session:', error);
      throw error;
    }
  }

  /**
   * Update an existing chat session
   */
  async updateChatSession(tenantId: string, sessionId: string, updates: Partial<ChatSession>): Promise<void> {
    try {
      await this.update(tenantId, 'chatSessions', sessionId, updates);
    } catch (error) {
      console.error('Error updating chat session:', error);
      throw error;
    }
  }

  /**
   * Get all chat sessions for a tenant
   */
  async getChatSessions(tenantId: string): Promise<ChatSession[]> {
    try {
      return await this.getAll<ChatSession>(tenantId, 'chatSessions', 'lastMessageAt', 'desc');
    } catch (error) {
      console.error('Error fetching chat sessions:', error);
      return [];
    }
  }

  /**
   * Get the active chat session for a tenant
   */
  async getActiveChatSession(tenantId: string): Promise<ChatSession | null> {
    try {
      const sessions = await this.getByField<ChatSession>(tenantId, 'chatSessions', 'isActive', true);
      return sessions.length > 0 ? sessions[0] : null;
    } catch (error) {
      console.error('Error fetching active chat session:', error);
      return null;
    }
  }

  /**
   * Save a chat message
   */
  async saveChatMessage(tenantId: string, messageData: Omit<ChatMessage, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      return await this.create(tenantId, 'chatMessages', messageData);
    } catch (error) {
      console.error('Error saving chat message:', error);
      throw error;
    }
  }

  /**
   * Get all messages for a specific chat session
   */
  async getChatMessages(tenantId: string, sessionId: string): Promise<ChatMessage[]> {
    try {
      const messages = await this.getByField<ChatMessage>(tenantId, 'chatMessages', 'sessionId', sessionId);
      return messages.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateA.getTime() - dateB.getTime();
      });
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      return [];
    }
  }

  /**
   * Get recent chat messages across all sessions
   */
  async getRecentChatMessages(tenantId: string, limit: number = 50): Promise<ChatMessage[]> {
    try {
      return await this.getAll<ChatMessage>(tenantId, 'chatMessages', 'createdAt', 'desc', limit);
    } catch (error) {
      console.error('Error fetching recent chat messages:', error);
      return [];
    }
  }

  /**
   * Delete a chat session and all its messages
   */
  async deleteChatSession(tenantId: string, sessionId: string): Promise<void> {
    try {
      // Delete all messages in the session
      const messages = await this.getChatMessages(tenantId, sessionId);
      for (const message of messages) {
        if (message.id) {
          await this.delete(tenantId, 'chatMessages', message.id);
        }
      }
      
      // Delete the session
      await this.delete(tenantId, 'chatSessions', sessionId);
    } catch (error) {
      console.error('Error deleting chat session:', error);
      throw error;
    }
  }

  /**
   * Generate a unique session ID
   */
  async generateSessionId(): Promise<string> {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get tenant subscription information
   */
  async getTenantSubscription(tenantId: string): Promise<any> {
    try {
      // Access tenant document directly from tenants collection
      const tenantRef = doc(db, 'tenants', tenantId);
      const tenantSnap = await getDoc(tenantRef);
      
      if (!tenantSnap.exists()) {
        return null;
      }
      
      const tenantData = tenantSnap.data();
      return tenantData?.subscription || null;
    } catch (error) {
      console.error('Error getting tenant subscription:', error);
      return null;
    }
  }

  /**
   * Get subscription plan by ID
   */
  async getSubscriptionPlan(planId: string): Promise<any> {
    try {
      // Access subscription plan directly from subscription_plans collection
      const planRef = doc(db, 'subscription_plans', planId);
      const planSnap = await getDoc(planRef);
      
      if (!planSnap.exists()) {
        return null;
      }
      
      const planData = planSnap.data();
      return { id: planSnap.id, ...planData };
    } catch (error) {
      console.error('Error getting subscription plan:', error);
      return null;
    }
  }

  /**
   * Check if tenant has access to AI chatbot feature
   */
  async hasAiChatbotAccess(tenantId: string): Promise<boolean> {
    try {
      // Check if we're in development mode (allow all access)
      if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEBUG === 'true') {
        return true;
      }
      
      const subscription = await this.getTenantSubscription(tenantId);
      
      if (!subscription) {
        return false; // No subscription means no access
      }

      // Check if subscription is active or in trial
      if (subscription.status !== 'active' && !subscription.isTrialActive) {
        return false;
      }

      // Get the plan details
      const plan = await this.getSubscriptionPlan(subscription.planId);
      
      if (!plan) {
        return false; // Plan not found
      }

      // Check if AI chatbot feature is enabled for this plan
      const hasAccess = plan.features?.aiChatbot === true;
      
      return hasAccess;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get all subscription plans
   */
  async getAllPlans(): Promise<any[]> {
    try {
      const plansRef = collection(db, 'subscription_plans');
      const q = query(plansRef, orderBy('sortOrder', 'asc'));
      const querySnapshot = await getDocs(q);
      const plans = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // If no plans exist, create a default trial plan
      if (plans.length === 0) {
        console.log('No subscription plans found, creating default trial plan...');
        await this.createDefaultTrialPlan();
        // Fetch plans again after creating default
        const newQuerySnapshot = await getDocs(q);
        return newQuerySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }
      
      return plans;
    } catch (error) {
      console.error('Error getting all plans:', error);
      return [];
    }
  }

  /**
   * Create a default trial plan for new users
   */
  private async createDefaultTrialPlan(): Promise<void> {
    try {
      console.log('🔧 Creating default trial plan...');
      
      const defaultPlan = {
        id: 'plan-default-trial',
        name: 'default-trial',
        displayName: 'Free Trial',
        description: 'Start with our free trial to explore all features',
        pricing: {
          monthly: 999,
          yearly: 9990,
          currency: 'INR'
        },
        features: {
          maxUsers: 3,
          maxOrders: 500,
          maxMenuItems: 50,
          maxStorage: 1024,
          inventoryManagement: true,
          paymentProcessing: true,
          analytics: true,
          multiLocation: false,
          api: false,
          whiteLabel: false,
          prioritySupport: false,
          customReports: false,
          aiChatbot: true
        },
        trial: {
          enabled: true,
          duration: 30, // 30 days trial
          features: ['inventoryManagement', 'paymentProcessing', 'analytics', 'aiChatbot']
        },
        isActive: true,
        isPopular: false,
        sortOrder: 1,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      const planRef = doc(db, 'subscription_plans', defaultPlan.id);
      
      // Check if plan already exists
      const existingPlan = await getDoc(planRef);
      if (existingPlan.exists()) {
        console.log('✅ Default trial plan already exists');
        return;
      }
      
      await setDoc(planRef, defaultPlan);
      console.log('✅ Default trial plan created successfully:', defaultPlan.id);
    } catch (error) {
      console.error('❌ Error creating default trial plan:', error);
      // Don't throw error as this is not critical for the app to function
    }
  }

  /**
   * Update tenant subscription after successful payment
   */
  async updateTenantSubscriptionAfterPayment(
    tenantId: string, 
    planId: string, 
    subscriptionId: string, 
    paymentId: string,
    billingCycle: 'monthly' | 'yearly' = 'monthly'
  ): Promise<void> {
    try {
      // Get the plan details
      const plan = await this.getSubscriptionPlan(planId);
      if (!plan) {
        throw new Error('Plan not found');
      }

      // Calculate subscription end date
      const startDate = new Date();
      const endDate = new Date();
      if (billingCycle === 'monthly') {
        endDate.setMonth(endDate.getMonth() + 1);
      } else {
        endDate.setFullYear(endDate.getFullYear() + 1);
      }

      // Update tenant subscription
      const subscriptionData = {
        planId,
        planName: plan.name || plan.displayName,
        status: 'active',
        billingCycle,
        startDate: Timestamp.fromDate(startDate),
        endDate: Timestamp.fromDate(endDate),
        subscriptionId,
        paymentId,
        assignedAt: Timestamp.now(),
        assignedBy: 'razorpay',
        isTrialActive: false
      };

      // Update tenant document
      const tenantRef = doc(db, 'tenants', tenantId);
      await updateDoc(tenantRef, {
        subscription: subscriptionData,
        updatedAt: Timestamp.now()
      });

      console.log(`✅ Tenant ${tenantId} subscription updated successfully`);
    } catch (error) {
      console.error('Error updating tenant subscription:', error);
      throw error;
    }
  }

  /**
   * Get tenant subscription status
   */
  async getTenantSubscriptionStatus(tenantId: string): Promise<{
    hasActiveSubscription: boolean;
    planName?: string;
    status?: string;
    endDate?: Date;
    isExpired: boolean;
  }> {
    try {
      const subscription = await this.getTenantSubscription(tenantId);
      
      if (!subscription) {
        return {
          hasActiveSubscription: false,
          isExpired: false
        };
      }

      const endDate = subscription.endDate?.toDate();
      const isExpired = endDate ? new Date() > endDate : false;

      return {
        hasActiveSubscription: subscription.status === 'active',
        planName: subscription.planName,
        status: subscription.status,
        endDate,
        isExpired
      };
    } catch (error) {
      console.error('Error getting subscription status:', error);
      return {
        hasActiveSubscription: false,
        isExpired: false
      };
    }
  }

  /**
   * Start a trial subscription for a tenant
   */
  async startTrial(tenantId: string, planId: string): Promise<void> {
    try {
      // Get the plan details
      const plan = await this.getSubscriptionPlan(planId);
      if (!plan) {
        throw new Error('Plan not found');
      }

      // Check if plan has trial enabled
      if (!plan.trial.enabled) {
        throw new Error('This plan does not offer a trial');
      }

      // Check if tenant already has a subscription
      const existingSubscription = await this.getTenantSubscription(tenantId);
      if (existingSubscription) {
        throw new Error('Tenant already has a subscription');
      }

      // Calculate trial end date - always use a fresh new Date() for trial start
      const now = new Date();
      const currentYear = now.getFullYear();
      const expectedYear = new Date().getFullYear();
      if (currentYear > expectedYear) {
        console.warn('Warning: System clock appears to be in the future:', now.toISOString());
      }
      // Add days using milliseconds for reliability
      const trialDurationMs = plan.trial.duration * 24 * 60 * 60 * 1000;
      const endDate = new Date(now.getTime() + trialDurationMs);

      // Debug logging
      console.log('Starting trial with details:', {
        tenantId,
        planId,
        planName: plan.displayName,
        trialDuration: plan.trial.duration,
        startDate: now.toISOString(),
        endDate: endDate.toISOString(),
        daysDifference: Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      });

      // Create subscription data with Firestore Timestamps
      const subscriptionData = {
        planId: planId,
        planName: plan.displayName,
        status: 'trial',
        startDate: Timestamp.fromDate(now),
        endDate: Timestamp.fromDate(endDate),
        isTrialActive: true,
        trialStartDate: Timestamp.fromDate(now),
        trialEndDate: Timestamp.fromDate(endDate),
        trialDaysRemaining: plan.trial.duration,
        assignedAt: Timestamp.fromDate(now),
        assignedBy: 'self-service',
        autoRenew: false
      };

      // Update tenant with trial subscription
      const tenantRef = doc(db, 'tenants', tenantId);
      await updateDoc(tenantRef, {
        subscription: subscriptionData,
        updatedAt: Timestamp.fromDate(now)
      });

      console.log(`✅ Trial started for tenant ${tenantId} on plan ${planId}`);
    } catch (error) {
      console.error('Error starting trial:', error);
      throw error;
    }
  }

  /**
   * Fix existing trial with incorrect end date
   */
  async fixTrialEndDate(tenantId: string): Promise<void> {
    try {
      const subscription = await this.getTenantSubscription(tenantId);
      if (!subscription || subscription.status !== 'trial') {
        throw new Error('No trial subscription found');
      }

      // Get the plan details
      const plan = await this.getSubscriptionPlan(subscription.planId);
      if (!plan) {
        throw new Error('Plan not found');
      }

      // Calculate correct trial end date from start date
      let startDate: Date;
      if (subscription.startDate.seconds) {
        startDate = new Date(subscription.startDate.seconds * 1000);
      } else if (subscription.startDate.toDate) {
        startDate = subscription.startDate.toDate();
      } else {
        startDate = new Date(subscription.startDate);
      }

      const correctEndDate = new Date(startDate);
      correctEndDate.setDate(startDate.getDate() + plan.trial.duration);

      console.log('Fixing trial end date:', {
        tenantId,
        originalEndDate: subscription.endDate,
        startDate: startDate.toISOString(),
        correctEndDate: correctEndDate.toISOString(),
        trialDuration: plan.trial.duration
      });

      // Update the subscription with correct end date
      const tenantRef = doc(db, 'tenants', tenantId);
      await updateDoc(tenantRef, {
        'subscription.endDate': Timestamp.fromDate(correctEndDate),
        'subscription.trialEndDate': Timestamp.fromDate(correctEndDate),
        'subscription.trialDaysRemaining': plan.trial.duration,
        updatedAt: Timestamp.now()
      });

      console.log(`✅ Trial end date fixed for tenant ${tenantId}`);
    } catch (error) {
      console.error('Error fixing trial end date:', error);
      throw error;
    }
  }

  subscribeToSupportChat(tenantId: string, callback: (messages: any[]) => void) {
    const messagesRef = collection(db, 'tenants', tenantId, 'supportChat');
    const q = query(messagesRef, orderBy('timestamp', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(messages);
    });
    return unsubscribe;
  }

  async sendSupportMessage(tenantId: string, message: any) {
    const messagesRef = collection(db, 'tenants', tenantId, 'supportChat');
    await addDoc(messagesRef, {
      ...message,
      timestamp: serverTimestamp(),
    });
  }

  // Notification methods
  async createNotification(tenantId: string, notificationData: Omit<Notification, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const notificationRef = collection(db, 'tenants', tenantId, 'notifications');
      const docRef = await addDoc(notificationRef, {
        ...notificationData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }

  async getNotifications(tenantId: string, limitCount?: number): Promise<Notification[]> {
    try {
      const notificationsRef = collection(db, 'tenants', tenantId, 'notifications');
      let q = query(notificationsRef, orderBy('createdAt', 'desc'));
      
      if (limitCount) {
        q = query(q, limit(limitCount));
      }
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Notification[];
    } catch (error) {
      console.error('Error getting notifications:', error);
      throw error;
    }
  }

  async getUnreadNotifications(tenantId: string): Promise<Notification[]> {
    try {
      const notificationsRef = collection(db, 'tenants', tenantId, 'notifications');
      const q = query(
        notificationsRef, 
        where('isRead', '==', false),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Notification[];
    } catch (error) {
      console.error('Error getting unread notifications:', error);
      throw error;
    }
  }

  async markNotificationAsRead(tenantId: string, notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, 'tenants', tenantId, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        isRead: true,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  async markNotificationsAsReadByType(tenantId: string, type: string): Promise<void> {
    try {
      const notificationsRef = collection(db, 'tenants', tenantId, 'notifications');
      const q = query(notificationsRef, where('type', '==', type), where('isRead', '==', false));
      const querySnapshot = await getDocs(q);
      
      const batch = writeBatch(db);
      querySnapshot.docs.forEach((doc) => {
        batch.update(doc.ref, {
          isRead: true,
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error marking notifications as read by type:', error);
      throw error;
    }
  }

  async markAllNotificationsAsRead(tenantId: string): Promise<void> {
    try {
      const unreadNotifications = await this.getUnreadNotifications(tenantId);
      const batch = writeBatch(db);
      
      unreadNotifications.forEach(notification => {
        const notificationRef = doc(db, 'tenants', tenantId, 'notifications', notification.id!);
        batch.update(notificationRef, {
          isRead: true,
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  async deleteNotification(tenantId: string, notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, 'tenants', tenantId, 'notifications', notificationId);
      await deleteDoc(notificationRef);
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  async deleteAllNotifications(tenantId: string): Promise<void> {
    try {
      const notifications = await this.getNotifications(tenantId);
      const batch = writeBatch(db);
      
      notifications.forEach(notification => {
        const notificationRef = doc(db, 'tenants', tenantId, 'notifications', notification.id!);
        batch.delete(notificationRef);
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error deleting all notifications:', error);
      throw error;
    }
  }

  subscribeToNotifications(tenantId: string, callback: (notifications: Notification[]) => void): Unsubscribe {
    try {
      const notificationsRef = collection(db, 'tenants', tenantId, 'notifications');
      const q = query(notificationsRef, orderBy('createdAt', 'desc'));
      
      return onSnapshot(q, (querySnapshot) => {
        const notifications = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Notification[];
        callback(notifications);
      });
    } catch (error) {
      console.error('Error subscribing to notifications:', error);
      return () => {};
    }
  }

  // Helper method to create system notifications
  async createSystemNotification(
    tenantId: string,
    title: string,
    message: string,
    type: Notification['type'] = 'info',
    priority: Notification['priority'] = 'medium',
    metadata?: Notification['metadata']
  ): Promise<string> {
    return this.createNotification(tenantId, {
      title,
      message,
      type,
      isRead: false,
      priority,
      metadata
    });
  }

  async getOrdersByTable(tenantId: string, tableNumber: string): Promise<Order[]> {
    try {
      const ordersRef = getTenantCollection(tenantId, 'orders');
      const q = query(
        ordersRef,
        where('tableNumber', '==', tableNumber),
        where('status', 'in', ['active', 'completed']),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Order[];
    } catch (error) {
      console.error('Error getting orders by table:', error);
      return [];
    }
  }

  // Add-on management methods
  
  /**
   * Get all add-on categories for a tenant
   */
  async getAddOnCategories(tenantId: string): Promise<AddOnCategory[]> {
    try {
      const collectionRef = getTenantCollection(tenantId, 'addOnCategories');
      const q = query(collectionRef, orderBy('displayOrder', 'asc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AddOnCategory[];
    } catch (error) {
      console.error('Error getting add-on categories:', error);
      throw error;
    }
  }

  /**
   * Get add-on categories organized in hierarchy
   */
  async getAddOnCategoriesHierarchy(tenantId: string): Promise<{
    topLevel: AddOnCategory[];
    subCategories: Map<string, AddOnCategory[]>;
  }> {
    try {
      const categories = await this.getAddOnCategories(tenantId);
      
      const topLevel = categories.filter(cat => !cat.isSubCategory);
      const subCategories = new Map<string, AddOnCategory[]>();
      
      categories.forEach(cat => {
        if (cat.isSubCategory && cat.parentCategoryId) {
          if (!subCategories.has(cat.parentCategoryId)) {
            subCategories.set(cat.parentCategoryId, []);
          }
          subCategories.get(cat.parentCategoryId)!.push(cat);
        }
      });
      
      return { topLevel, subCategories };
    } catch (error) {
      console.error('Error getting add-on categories hierarchy:', error);
      throw error;
    }
  }

  /**
   * Get sub-categories for a specific parent category
   */
  async getSubCategories(tenantId: string, parentCategoryId: string): Promise<AddOnCategory[]> {
    try {
      const collectionRef = getTenantCollection(tenantId, 'addOnCategories');
      const q = query(
        collectionRef, 
        where('parentCategoryId', '==', parentCategoryId),
        orderBy('displayOrder', 'asc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AddOnCategory[];
    } catch (error) {
      console.error('Error getting sub-categories:', error);
      throw error;
    }
  }

  /**
   * Get add-on options for a specific category
   */
  async getAddOnOptions(tenantId: string, categoryId: string): Promise<AddOnOption[]> {
    try {
      const collectionRef = getTenantCollection(tenantId, 'addOnOptions');
      const q = query(
        collectionRef, 
        where('categoryId', '==', categoryId),
        orderBy('displayOrder', 'asc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AddOnOption[];
    } catch (error) {
      console.error('Error getting add-on options:', error);
      throw error;
    }
  }

  /**
   * Get all add-on options for a tenant
   */
  async getAllAddOnOptions(tenantId: string): Promise<AddOnOption[]> {
    try {
      const collectionRef = getTenantCollection(tenantId, 'addOnOptions');
      const q = query(collectionRef, orderBy('displayOrder', 'asc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AddOnOption[];
    } catch (error) {
      console.error('Error getting all add-on options:', error);
      throw error;
    }
  }

  /**
   * Get add-ons configuration for a specific menu item
   */
  async getMenuItemAddOns(tenantId: string, menuItemId: string): Promise<MenuItemAddOns | null> {
    try {
      const collectionRef = getTenantCollection(tenantId, 'menuItemAddOns');
      const q = query(collectionRef, where('menuItemId', '==', menuItemId));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) return null;
      
      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      } as MenuItemAddOns;
    } catch (error) {
      console.error('Error getting menu item add-ons:', error);
      throw error;
    }
  }

  /**
   * Create or update add-on category
   */
  async saveAddOnCategory(tenantId: string, category: Omit<AddOnCategory, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      console.log('Saving add-on category:', { tenantId, category });
      const collectionRef = getTenantCollection(tenantId, 'addOnCategories');
      console.log('Collection reference:', collectionRef);
      
      const docData = {
        ...category,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      console.log('Document data to save:', docData);
      
      const docRef = await addDoc(collectionRef, docData);
      console.log('Document created successfully:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error saving add-on category:', error);
      console.error('Error details:', {
        code: (error as any).code,
        message: (error as any).message,
        stack: (error as any).stack
      });
      throw error;
    }
  }

  /**
   * Update existing add-on category
   */
  async updateAddOnCategory(tenantId: string, categoryId: string, updates: Partial<Omit<AddOnCategory, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> {
    try {
      const categoryRef = doc(getTenantCollection(tenantId, 'addOnCategories'), categoryId);
      await updateDoc(categoryRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating add-on category:', error);
      throw error;
    }
  }

  /**
   * Create or update add-on option
   */
  async saveAddOnOption(tenantId: string, option: Omit<AddOnOption, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const collectionRef = getTenantCollection(tenantId, 'addOnOptions');
      const docRef = await addDoc(collectionRef, {
        ...option,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error saving add-on option:', error);
      throw error;
    }
  }

  /**
   * Update existing add-on option
   */
  async updateAddOnOption(tenantId: string, optionId: string, updates: Partial<Omit<AddOnOption, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> {
    try {
      const optionRef = doc(getTenantCollection(tenantId, 'addOnOptions'), optionId);
      await updateDoc(optionRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating add-on option:', error);
      throw error;
    }
  }



  /**
   * Configure add-ons for a menu item
   */
  async configureMenuItemAddOns(tenantId: string, menuItemId: string, addOnCategories: Array<{
    categoryId: string;
    categoryName: string;
    isRequired: boolean;
    minSelections: number;
    maxSelections: number;
    displayOrder: number;
  }>): Promise<void> {
    try {
      const collectionRef = getTenantCollection(tenantId, 'menuItemAddOns');
      
      // Check if configuration already exists
      const existingQuery = query(collectionRef, where('menuItemId', '==', menuItemId));
      const existingSnapshot = await getDocs(existingQuery);
      
      if (!existingSnapshot.empty) {
        // Update existing configuration
        const docRef = existingSnapshot.docs[0].ref;
        await updateDoc(docRef, {
          addOnCategories,
          updatedAt: new Date()
        });
      } else {
        // Create new configuration
        await addDoc(collectionRef, {
          menuItemId,
          addOnCategories,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
      
      // Update menu item to indicate it supports add-ons
      const menuItemRef = doc(getTenantCollection(tenantId, 'menuItems'), menuItemId);
      await updateDoc(menuItemRef, {
        supportsAddOns: addOnCategories.length > 0,
        addOnCategories: addOnCategories.map(cat => cat.categoryId),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error configuring menu item add-ons:', error);
      throw error;
    }
  }

  /**
   * Delete add-on category (only if not used by any menu items)
   */
  async deleteAddOnCategory(tenantId: string, categoryId: string): Promise<void> {
    try {
      // Check if category is used by any menu items
      const menuItemsRef = getTenantCollection(tenantId, 'menuItems');
      const menuItemsQuery = query(menuItemsRef, where('addOnCategories', 'array-contains', categoryId));
      const menuItemsSnapshot = await getDocs(menuItemsQuery);
      
      if (!menuItemsSnapshot.empty) {
        throw new Error('Cannot delete category: it is used by menu items');
      }
      
      // Delete all options in this category
      const optionsRef = getTenantCollection(tenantId, 'addOnOptions');
      const optionsQuery = query(optionsRef, where('categoryId', '==', categoryId));
      const optionsSnapshot = await getDocs(optionsQuery);
      
      const deletePromises = optionsSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      // Delete the category
      const categoryRef = doc(getTenantCollection(tenantId, 'addOnCategories'), categoryId);
      await deleteDoc(categoryRef);
    } catch (error) {
      console.error('Error deleting add-on category:', error);
      throw error;
    }
  }

  /**
   * Delete add-on option (only if not used by any active orders)
   */
  async deleteAddOnOption(tenantId: string, optionId: string): Promise<void> {
    try {
      // Check if option is used by any active orders
      const ordersRef = getTenantCollection(tenantId, 'orders');
      const ordersQuery = query(ordersRef, where('status', '==', 'active'));
      const ordersSnapshot = await getDocs(ordersQuery);
      
      let isOptionUsed = false;
      for (const orderDoc of ordersSnapshot.docs) {
        const order = orderDoc.data();
        for (const item of order.items || []) {
          if (item.addOnSelections) {
            for (const selection of item.addOnSelections) {
              if (selection.selectedOptions.some((opt: { optionId: string }) => opt.optionId === optionId)) {
                isOptionUsed = true;
                break;
              }
            }
          }
          if (isOptionUsed) break;
        }
        if (isOptionUsed) break;
      }
      
      if (isOptionUsed) {
        throw new Error('Cannot delete option: it is used by active orders');
      }
      
      // Delete the option
      const optionRef = doc(getTenantCollection(tenantId, 'addOnOptions'), optionId);
      await deleteDoc(optionRef);
    } catch (error) {
      console.error('Error deleting add-on option:', error);
      throw error;
    }
  }

  /**
   * Bulk create/update raw materials from CSV import
   */
  async bulkCreateRawMaterials(
    tenantId: string, 
    materials: Partial<RawMaterial>[], 
    mode: 'create' | 'update' | 'createAndUpdate'
  ): Promise<{ success: boolean; created: number; updated: number; errors: any[]; totalRows: number }> {
    try {
      const collectionRef = getTenantCollection(tenantId, 'rawMaterials');
      const batch = writeBatch(db);
      
      let created = 0;
      let updated = 0;
      const errors: any[] = [];
      
      // Get existing materials for duplicate checking
      const existingMaterials = await this.getAll<RawMaterial>(tenantId, 'rawMaterials');
      const existingByName = new Map(existingMaterials.map(m => [m.name.toLowerCase(), m]));
      
      for (let i = 0; i < materials.length; i++) {
        const material = materials[i];
        
        try {
          // Validate required fields
          if (!material.name || !material.category || !material.supplier) {
            errors.push({
              row: i + 1,
              field: 'Required fields',
              message: 'Name, category, and supplier are required',
              value: material
            });
            continue;
          }
          
          // Check if material already exists
          const existingMaterial = existingByName.get(material.name.toLowerCase());
          
          if (existingMaterial) {
            // Update existing material
            if (mode === 'create' || mode === 'createAndUpdate') {
              const updatedData = {
                ...material,
                id: existingMaterial.id,
                updatedAt: new Date()
              };
              
              const docRef = doc(collectionRef, existingMaterial.id);
              batch.update(docRef, updatedData);
              updated++;
            } else if (mode === 'update') {
              // Skip if only update mode and material exists
              continue;
            }
          } else {
            // Create new material
            if (mode === 'update' || mode === 'createAndUpdate') {
              const newData = {
                ...material,
                createdAt: new Date(),
                updatedAt: new Date()
              };
              
              const docRef = doc(collectionRef);
              batch.set(docRef, newData);
              created++;
            } else if (mode === 'create') {
              // Skip if only create mode and material doesn't exist
              continue;
            }
          }
        } catch (error) {
          errors.push({
            row: i + 1,
            field: 'Processing error',
            message: `Error processing material: ${error}`,
            value: material
          });
        }
      }
      
      // Execute batch write
      await batch.commit();
      
      return {
        success: errors.length === 0,
        created,
        updated,
        errors,
        totalRows: materials.length
      };
    } catch (error) {
      console.error('Error in bulk create raw materials:', error);
      throw error;
    }
  }

  /**
   * Bulk create/update menu items from CSV import
   */
  async bulkCreateMenuItems(
    tenantId: string, 
    items: Partial<MenuItem>[], 
    mode: 'create' | 'update' | 'createAndUpdate'
  ): Promise<{ success: boolean; created: number; updated: number; errors: any[]; totalRows: number }> {
    try {
      const uniqueCategoryNames = new Map<string, string>();
      for (const item of items) {
        const categoryName = item.category?.trim();
        if (!categoryName) continue;

        const normalizedCategoryName = categoryName.toLowerCase();
        if (!uniqueCategoryNames.has(normalizedCategoryName)) {
          uniqueCategoryNames.set(normalizedCategoryName, categoryName);
        }
      }

      const existingCategories = await this.getCategories(tenantId, 'menuItem');
      const categoryNameMap = new Map(existingCategories.map((category) => [
        category.name.trim().toLowerCase(),
        category.name.trim()
      ]));

      const categoryEntries = Array.from(uniqueCategoryNames);
      for (let index = 0; index < categoryEntries.length; index++) {
        const [normalizedCategoryName, categoryName] = categoryEntries[index];
        if (!categoryNameMap.has(normalizedCategoryName)) {
          await this.createCategory(tenantId, {
            name: categoryName,
            type: 'menuItem'
          });
          categoryNameMap.set(normalizedCategoryName, categoryName);
        }
      }

      const collectionRef = getTenantCollection(tenantId, 'menuItems');
      const batch = writeBatch(db);
      
      let created = 0;
      let updated = 0;
      const errors: any[] = [];
      
      // Get existing menu items for duplicate checking
      const existingItems = await this.getAll<MenuItem>(tenantId, 'menuItems');
      const existingByName = new Map(existingItems.map(item => [item.name.toLowerCase(), item]));
      
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const normalizedCategoryName = item.category?.trim().toLowerCase();
        const resolvedCategoryName = normalizedCategoryName
          ? categoryNameMap.get(normalizedCategoryName) || item.category?.trim()
          : item.category;
        
        try {
          // Validate required fields
          if (!item.name || !resolvedCategoryName || !item.price || !item.preparationTime) {
            errors.push({
              row: i + 1,
              field: 'Required fields',
              message: 'Name, category, price, and preparation time are required',
              value: item
            });
            continue;
          }
          
          // Check if item already exists
          const existingItem = existingByName.get(item.name.toLowerCase());
          
          if (existingItem) {
            // Update existing item
            if (mode === 'create' || mode === 'createAndUpdate') {
              const updatedData = {
                ...item,
                category: resolvedCategoryName,
                id: existingItem.id,
                ingredients: existingItem.ingredients, // Preserve existing ingredients
                updatedAt: new Date()
              };
              
              const docRef = doc(collectionRef, existingItem.id);
              batch.update(docRef, updatedData);
              updated++;
            } else if (mode === 'update') {
              // Skip if only update mode and item exists
              continue;
            }
          } else {
            // Create new item
            if (mode === 'update' || mode === 'createAndUpdate') {
              const newData = {
                ...item,
                category: resolvedCategoryName,
                ingredients: [], // Start with empty ingredients
                createdAt: new Date(),
                updatedAt: new Date()
              };
              
              const docRef = doc(collectionRef);
              batch.set(docRef, newData);
              created++;
            } else if (mode === 'create') {
              // Skip if only create mode and item doesn't exist
              continue;
            }
          }
        } catch (error) {
          errors.push({
            row: i + 1,
            field: 'Processing error',
            message: `Error processing menu item: ${error}`,
            value: item
          });
        }
      }
      
      // Execute batch write
      await batch.commit();
      
      return {
        success: errors.length === 0,
        created,
        updated,
        errors,
        totalRows: items.length
      };
    } catch (error) {
      console.error('Error in bulk create menu items:', error);
      throw error;
    }
  }

  /**
   * CSV Sync: Upsert raw materials with stock change tracking
   */
  async csvSyncRawMaterials(
    tenantId: string,
    materials: Partial<RawMaterial>[],
    performedBy: string = 'CSV Import'
  ): Promise<{ success: boolean; created: number; updated: number; errors: any[]; totalRows: number }> {
    try {
      let created = 0;
      let updated = 0;
      const errors: any[] = [];
      const stockChanges: Array<{ materialId: string; materialName: string; oldStock: number; newStock: number; baseUnit: string }> = [];
      
      // Get existing materials for matching
      const existingMaterials = await this.getAll<RawMaterial>(tenantId, 'rawMaterials');
      const existingBySku = new Map(existingMaterials
        .filter(m => m.sku)
        .map(m => [m.sku!.toLowerCase(), m] as [string, RawMaterial]));
      const existingByName = new Map(existingMaterials.map(m => [m.name.toLowerCase(), m] as [string, RawMaterial]));
      
      for (let i = 0; i < materials.length; i++) {
        const material = materials[i];
        
        try {
          // Validate required fields
          if (!material.name) {
            errors.push({
              row: i + 1,
              field: 'Name',
              message: 'Name is required',
              value: material
            });
            continue;
          }
          
          // Find existing material by SKU first, then by name
          let existingMaterial: RawMaterial | undefined;
          if (material.sku) {
            existingMaterial = existingBySku.get(material.sku.toLowerCase());
          }
          if (!existingMaterial) {
            existingMaterial = existingByName.get(material.name.toLowerCase());
          }
          
          if (existingMaterial) {
            // Update existing material
            const oldStock = existingMaterial.currentStock;
            const newStock = material.currentStock || existingMaterial.currentStock;
            const stockDelta = newStock - oldStock;
            
            // Update the material
            await this.update<RawMaterial>(tenantId, 'rawMaterials', existingMaterial.id!, {
              ...material,
              updatedAt: new Date()
            });
            updated++;
            
            // Track stock changes for transaction creation
            if (Math.abs(stockDelta) > 0.001) { // Use small epsilon for floating point comparison
              stockChanges.push({
                materialId: existingMaterial.id!,
                materialName: existingMaterial.name,
                oldStock,
                newStock,
                baseUnit: existingMaterial.baseUnit
              });
            }
          } else {
            // Create new material - ensure required fields are present
            const newData: Omit<RawMaterial, 'id' | 'createdAt' | 'updatedAt'> = {
              name: material.name!,
              category: material.category || 'Uncategorized',
              currentStock: material.currentStock || 0,
              minStock: material.minStock || 0,
              maxStock: material.maxStock || 0,
              baseUnit: material.baseUnit || 'piece',
              costPerUnit: material.costPerUnit || 0,
              unitConversions: material.unitConversions || [],
              active: material.active !== undefined ? material.active : true,
              sku: material.sku,
              supplier: material.supplier,
              supplierContact: material.supplierContact,
              location: material.location,
              description: material.description,
              expiryDate: material.expiryDate,
              // Legacy fields for backward compatibility
              mainUnit: material.baseUnit || 'piece',
              mainUnitQuantity: 0,
              subUnit: material.baseUnit || 'piece',
              subUnitsPerMainUnit: 1,
              costPerMainUnit: material.costPerUnit || 0,
              costPerSubUnit: material.costPerUnit || 0,
              defaultPurchaseUnit: material.baseUnit || 'piece',
              defaultSaleUnit: material.baseUnit || 'piece'
            };
            
            const materialId = await this.create<RawMaterial>(tenantId, 'rawMaterials', newData);
            created++;
          }
        } catch (error) {
          errors.push({
            row: i + 1,
            field: 'Processing error',
            message: `Error processing material: ${error}`,
            value: material
          });
        }
      }
      
      // Create adjustment transactions for stock changes
      for (const change of stockChanges) {
        try {
          const stockDelta = change.newStock - change.oldStock;
          const transactionData: Omit<InventoryTransaction, 'id' | 'createdAt' | 'updatedAt'> = {
            itemId: change.materialId,
            itemName: change.materialName,
            type: stockDelta > 0 ? 'in' : 'out',
            quantity: Math.abs(stockDelta),
            unit: change.baseUnit,
            reason: 'CSV sync',
            performedBy,
            notes: `Stock updated from ${change.oldStock} to ${change.newStock} via CSV import`,
            transactionSource: 'adjustment'
          };
          
          await this.createInventoryTransaction(tenantId, transactionData);
        } catch (error) {
          console.error('Error creating adjustment transaction:', error);
          errors.push({
            row: 0,
            field: 'Transaction creation',
            message: `Failed to create transaction for ${change.materialName}: ${error}`,
            value: change
          });
        }
      }
      
      return {
        success: errors.length === 0,
        created,
        updated,
        errors,
        totalRows: materials.length
      };
    } catch (error) {
      console.error('Error in CSV sync raw materials:', error);
      throw error;
    }
  }

  /**
   * Find duplicates by name in a collection
   */
  async findDuplicatesByName(tenantId: string, collection: string, names: string[]): Promise<Map<string, any>> {
    try {
      const collectionRef = getTenantCollection(tenantId, collection);
      const duplicates = new Map();
      
      for (const name of names) {
        const nameQuery = query(collectionRef, where('name', '==', name));
        const snapshot = await getDocs(nameQuery);
        
        if (!snapshot.empty) {
          duplicates.set(name, snapshot.docs[0].data());
        }
      }
      
      return duplicates;
    } catch (error) {
      console.error('Error finding duplicates:', error);
      throw error;
    }
  }

}

export const dataService = new DataService();

// Expose dataService globally for debugging in development
if (process.env.NODE_ENV === 'development') {
  (window as any).dataService = dataService;
} 